import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { ArrowLeft, MapPin, Clock, Users, TrendingUp, DollarSign, Phone, Globe, Instagram, Star, Pencil, X, ChevronRight, ChevronLeft, Building2, Plus, Download, Upload } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, LabelList } from 'recharts';
import B2BRevenuePanel from '@/components/B2BRevenuePanel';
import MonthlyBillingTab from '@/components/MonthlyBillingTab';
import RevenueTrendWidget from '@/components/RevenueTrendWidget';
import ChannelMixWidget from '@/components/ChannelMixWidget';
import PaceIndicator from '@/components/PaceIndicator';
import SopListenPills from '@/components/SopListenPills';

const fC = (v) => {
  if (!v || v === 0) return '0';
  const n = Number(v);
  if (n >= 10000000) return `${(n / 10000000).toFixed(2).replace(/\.?0+$/, '')}Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(1).replace(/\.?0+$/, '')}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return n.toLocaleString('en-IN');
};

/**
 * Cloudinary doc URL fixer.
 *
 * Legacy bug (pre-2026-05-11): PDFs uploaded via /api/cloudinary/upload
 * (which forces resource_type=image) got stored at `.../image/upload/.../foo.pdf`.
 * Cloudinary blocks PDF delivery from `image/` paths by default for
 * anti-phishing — the browser sees a 401/403, hence "Failed to load".
 *
 * Fix forward: new uploads use /api/cloudinary/upload-doc (resource_type=auto)
 * which puts PDFs at `.../raw/upload/.../foo.pdf` (delivers fine).
 * Fix legacy: rewrite at render time. Cloudinary serves the same blob
 * from both paths when a PDF is uploaded as image type.
 */
const fixCloudinaryDocUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (!/\.pdf(\?|$)/i.test(url)) return url; // only PDFs
  if (!url.includes('/image/upload/')) return url; // already raw or external
  return url.replace('/image/upload/', '/raw/upload/');
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OutletIntelPage() {
  const { divId, outletId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [profile, setProfile] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [people, setPeople] = useState(null);
  const [costCentre, setCostCentre] = useState(null);
  const [clientId, setClientId] = useState(null);   // matching clients_registry row id, if any
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(searchParams.get('tab') || 'overview');
  const [editProfile, setEditProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const tabBarRef = useRef(null);
  const [canScrollL, setCanScrollL] = useState(false);
  const [canScrollR, setCanScrollR] = useState(false);

  const updateScrollArrows = () => {
    const el = tabBarRef.current;
    if (!el) return;
    setCanScrollL(el.scrollLeft > 4);
    setCanScrollR(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  const scrollTabs = (dir) => {
    const el = tabBarRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.7), behavior: 'smooth' });
  };

  const outletName = decodeURIComponent(outletId);
  const encodedName = encodeURIComponent(outletName);

  // If this "outlet" is actually a B2B client (has a clients_registry entry
  // in this division or any child), send the user to the Client Dossier. The
  // cafe-style Outlet Intel page is meaningless for B2B clients.
  //
  // Similarly, if the outlet name actually corresponds to a CHILD DIVISION
  // (e.g. "Club Social B2B" outlet under `cs` is really the cs-b2b sub-division
  // — a contract-style portfolio with clients & payment schedules, NOT a
  // restaurant with F&B/Labour/reviews) — send the user to that division page
  // which already renders the correct client-view layout.
  const [redirectResolved, setRedirectResolved] = useState(false);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        // 1. Sub-division match — redirect to /division/{child_id}
        const divs = await api.get('/divisions?include_children=1').catch(() => ({ data: [] }));
        const norm = (s) => (s || '').toLowerCase().trim();
        const match = (divs.data || []).find(
          (d) =>
            d.parent_division_id === divId &&
            (norm(d.name) === norm(outletName) ||
             norm(d.entity_name) === norm(outletName))
        );
        if (match) {
          navigate(`/division/${match._id || match.id}`, { replace: true });
          return;
        }
      } catch { /* silent */ }
      try {
        // 2. Client registry match — redirect to /client/{id}
        const r = await api.get(`/clients-registry?division_id=${divId}`);
        if (cancel) return;
        const match = (r.data || []).find(
          (c) => (c.client_name || '').toLowerCase() === outletName.toLowerCase()
        );
        if (match && match.id) {
          navigate(`/client/${match.id}`, { replace: true });
          return;
        }
      } catch (e) { /* silent */ }
      if (!cancel) setRedirectResolved(true);
    })();
    return () => { cancel = true; };
  }, [divId, outletName, navigate]);

  const refreshOverview = async () => {
    try {
      const ovRes = await api.get(`/outlet-intel/${encodedName}/overview?division_id=${divId}`);
      setOverview(ovRes.data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [ovRes, prRes, rvRes, ppRes] = await Promise.all([
          api.get(`/outlet-intel/${encodedName}/overview?division_id=${divId}`),
          api.get(`/outlet-intel/${encodedName}/profile`),
          api.get(`/outlet-intel/${encodedName}/revenue?period=last_30d`),
          api.get(`/outlet-intel/${encodedName}/people`),
        ]);
        setOverview(ovRes.data);
        setProfile(prRes.data);
        setProfileForm(prRes.data);
        setRevenue(rvRes.data);
        setPeople(ppRes.data);
        // For cost centres, pull the cost summary too
        if (ovRes.data?.is_cost_centre || ovRes.data?.outlet_type === 'cost_centre') {
          try {
            const ccRes = await api.get(`/outlet-intel/${encodedName}/cost-centre-summary`);
            setCostCentre(ccRes.data);
          } catch (e) { /* non-blocking */ }
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [encodedName]);

  // For B2B divisions (lc/cc-clubs/cc-ops/cs), detect whether this "outlet"
  // is actually a client — if so, show a banner linking to the richer
  // Client Dossier page (Contacts, Contracts, Documents, Meeting Notes,
  // Payments, Revenue History). Fixes Dipali's 24-Apr-2026 confusion where
  // clicking RedBeryl landed on the Ops-style outlet page.
  useEffect(() => {
    const B2B = ['lc', 'cc-clubs', 'cc-ops', 'cs'];
    if (!B2B.includes(divId) || !outletId) return;
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/clients-registry?division_id=${divId}`);
        if (!alive) return;
        const decoded = decodeURIComponent(outletId || '').toLowerCase().trim();
        const match = (r.data || []).find((c) =>
          (c.client_name || '').toLowerCase().trim() === decoded ||
          (c.name || '').toLowerCase().trim() === decoded
        );
        if (match && (match.id || match._id)) setClientId(match.id || match._id);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [divId, outletId]);

  // Init + update tab scroll arrows when overview loads, section changes, or window resizes
  useEffect(() => {
    // Wait for tabs to render
    const t = setTimeout(updateScrollArrows, 50);
    window.addEventListener('resize', updateScrollArrows);
    return () => { clearTimeout(t); window.removeEventListener('resize', updateScrollArrows); };
  }, [overview, activeSection, loading]);

  const saveProfile = async () => {
    try {
      await api.put(`/outlet-intel/${encodedName}/profile`, profileForm);
      setProfile(profileForm);
      setEditProfile(false);
    } catch {}
  };

  if (loading || !redirectResolved) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-[#2A7B88] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!overview) return (
    <div className="text-center py-16">
      <p className="text-sm" style={{ color: '#9CA3AF' }}>Outlet not found: {outletName}</p>
      <button onClick={() => navigate(`/division/${divId}`)} className="mt-4 text-sm underline" style={{ color: '#2A7B88' }}>Back to Division</button>
    </div>
  );

  const ov = overview;
  const rv = ov.revenue;
  const healthColors = { green: '#2D8B5E', amber: '#D97706', red: '#DC2626' };
  const healthBg = { green: '#ECFDF5', amber: '#FEF3C7', red: '#FEF2F2' };
  const typeLabel = { fnb: 'F&B Restaurant', stays: 'Accommodation', club: 'Club', miracles: 'Landscaping', hq: 'Head Office', cost_centre: 'Cost Centre', services: 'B2B Services', office: 'Corporate Office' };
  const isCC = ov.outlet_type === 'cost_centre' || ov.is_cost_centre;
  const isServices = ov.outlet_type === 'services' || ov.outlet_type === 'office';

  const sections = isCC
    ? [
        { id: 'overview', label: 'Overview' },
        { id: 'people', label: 'People' },
        { id: 'assets', label: 'Assets' },
        { id: 'compliance', label: 'Compliance' },
        { id: 'workorders', label: 'Work Orders' },
        { id: 'pm', label: 'Preventive Maintenance' },
        { id: 'sops', label: 'SOPs' },
        { id: 'profile', label: 'Profile' },
      ]
    : isServices
    ? [
        // B2B services outlet — client contracts, monthly invoicing, no kitchen/restaurant ops
        { id: 'overview', label: 'Overview' },
        { id: 'revenue', label: 'Monthly Revenue' },
        { id: 'people', label: 'People' },
        { id: 'compliance', label: 'Compliance' },
        { id: 'sops', label: 'SOPs' },
        { id: 'profile', label: 'Profile' },
      ]
    : [
        { id: 'overview', label: 'Overview' },
        { id: 'revenue', label: 'Revenue' },
        ...(ov.outlet_type === 'fnb' || ov.outlet_type === 'stays' ? [{ id: 'cost', label: 'Cost' }] : []),
        { id: 'reviews', label: 'Reviews' },
        { id: 'people', label: 'People' },
        { id: 'compliance', label: 'Compliance' },
        { id: 'assets', label: 'Assets' },
        { id: 'workorders', label: 'Work Orders' },
        { id: 'pm', label: 'Preventive Maintenance' },
        { id: 'operations', label: 'Operations & Incidents' },
        { id: 'crockery', label: 'Crockery & Linen' },
        ...(ov.outlet_type === 'fnb' || ov.outlet_type === 'club' ? [{ id: 'bar', label: 'Bar Variance' }] : []),
        ...(ov.outlet_type === 'stays' ? [{ id: 'accommodation', label: 'Rooms' }] : []),
        { id: 'sops', label: 'SOPs' },
        { id: 'common', label: 'Common Areas' },
        { id: 'profile', label: 'Profile' },
      ];

  return (
    <div className="max-w-6xl mx-auto space-y-5 px-2 sm:px-4" data-testid="outlet-intel-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs" style={{ color: '#9CA3AF' }}>
        <button onClick={() => navigate(`/division/${divId}`)} className="flex items-center gap-1 hover:text-[#2A7B88] transition-colors" data-testid="back-to-division">
          <ArrowLeft size={14} />
          <span className="hidden sm:inline">{divId === 'me' ? 'MindEscapes' : divId.toUpperCase()}</span>
        </button>
        <ChevronRight size={12} />
        <span className="font-medium" style={{ color: '#1B2A4A' }}>{outletName}</span>
      </div>

      {/* === HEADER === */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 pb-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ color: '#1B2A4A' }} data-testid="outlet-title">{outletName}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className="text-[11px] px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: '#F7F5F2', color: '#6B7280' }}>
              {typeLabel[ov.outlet_type] || ov.outlet_type}
            </span>
            {ov.location && <span className="flex items-center gap-1 text-xs" style={{ color: '#6B7280' }}><MapPin size={12} />{ov.location}</span>}
            {ov.owner && (
              <span className="text-xs" style={{ color: '#6B7280' }} data-testid="outlet-owner-line">
                Owner: <strong>{ov.owner}</strong>
                {Array.isArray(ov.co_owners) && ov.co_owners.length > 0 && (
                  <> · Co-owner{ov.co_owners.length > 1 ? 's' : ''}: <strong>{ov.co_owners.map(c => (c && c.name) || c).filter(Boolean).join(', ')}</strong></>
                )}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {ov.health === 'insufficient_data' ? (
            <span className="text-[11px] px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }} data-testid="health-badge">
              Insufficient data
            </span>
          ) : (
            <span className="text-xs px-3 py-1 rounded-full font-bold" style={{ backgroundColor: healthBg[ov.health], color: healthColors[ov.health] }} data-testid="health-badge">
              {ov.health_score}/10 {ov.health === 'green' ? 'Healthy' : ov.health === 'amber' ? 'Watch' : 'Alert'}
            </span>
          )}
        </div>
      </div>

      {/* === SECTION NAV (horizontal scroll on mobile) === */}
      <div className="relative border-b" style={{ borderColor: '#E5E1DC' }}>
        {/* Client Dossier jump-banner — only on B2B divisions where the
            outlet has a matching clients_registry row. Tells the CEO that
            richer contract/contact/payment info lives on a dedicated page. */}
        {clientId && (
          <div className="mb-3 rounded-xl border p-3 flex items-center justify-between gap-3" style={{ borderColor: '#2A7B88', backgroundColor: '#ECFDF5' }} data-testid="client-dossier-banner">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-full" style={{ backgroundColor: '#2A7B88' }}>
                <Building2 size={18} color="#fff" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#065F46' }}>Client relationship</p>
                <p className="text-sm font-medium" style={{ color: '#065F46' }}>
                  Open this client's full dossier — Contacts, Contracts, Documents, Meeting Notes, Payments, Revenue History
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate(`/client/${clientId}`)}
              className="shrink-0 px-4 py-2 text-xs font-semibold text-white rounded-lg flex items-center gap-1.5 hover:opacity-90 transition"
              style={{ backgroundColor: '#2A7B88' }}
              data-testid="open-client-dossier-btn"
            >
              Open dossier <ChevronRight size={14} />
            </button>
          </div>
        )}
        {canScrollL && (
          <button onClick={() => scrollTabs(-1)} aria-label="Scroll tabs left" className="absolute left-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-8 h-8 rounded-full shadow-md border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E5E1DC', color: '#1B2A4A' }} data-testid="tab-scroll-left">
            <ChevronLeft size={16} />
          </button>
        )}
        {canScrollR && (
          <button onClick={() => scrollTabs(1)} aria-label="More tabs — scroll right" className="absolute right-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-8 h-8 rounded-full shadow-md border animate-pulse" style={{ backgroundColor: '#2A7B88', borderColor: '#2A7B88', color: '#FFFFFF' }} data-testid="tab-scroll-right">
            <ChevronRight size={16} />
          </button>
        )}
        {/* Left / Right fade overlays to hint scrollability */}
        {canScrollL && <div className="absolute left-0 top-0 bottom-0 w-10 pointer-events-none" style={{ background: 'linear-gradient(to right, #F7F5F2, transparent)' }} />}
        {canScrollR && <div className="absolute right-0 top-0 bottom-0 w-14 pointer-events-none" style={{ background: 'linear-gradient(to left, #F7F5F2, transparent)' }} />}
        <div
          ref={tabBarRef}
          onScroll={updateScrollArrows}
          className="flex gap-1.5 overflow-x-auto pb-2 px-1 scrollbar-hide"
          style={{ scrollbarWidth: 'none' }}
        >
          {sections.map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              className={`whitespace-nowrap px-4 py-2 text-sm rounded-full transition-all shrink-0 ${activeSection === s.id ? 'font-semibold text-white shadow-sm' : 'font-medium text-[#6B7280] hover:bg-[#F7F5F2]'}`}
              style={activeSection === s.id ? { backgroundColor: '#2A7B88' } : {}}
              data-testid={`section-${s.id}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* === SECTION 1: OVERVIEW CARD === */}
      {activeSection === 'overview' && (
        <div className="space-y-4" data-testid="section-overview-content">
          {/* Performance Score Banner (Section 10) */}
          {ov.score_breakdown && ov.health !== 'insufficient_data' && ov.health_score !== null && (
            <div className="rounded-xl border p-4" style={{ borderColor: '#E5E1DC', backgroundColor: ov.health === 'green' ? '#ECFDF5' : ov.health === 'amber' ? '#FEF3C7' : '#FEF2F2' }} data-testid="performance-score-card">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#6B7280' }}>Performance Score</p>
                  <div className="flex items-baseline gap-1">
                    <p className="text-3xl font-bold" style={{ color: ov.health === 'green' ? '#2D8B5E' : ov.health === 'amber' ? '#D97706' : '#DC2626' }}>{ov.health_score}</p>
                    <p className="text-sm font-medium" style={{ color: '#6B7280' }}>/ 10</p>
                    <span className="ml-2 text-[10px] uppercase px-2 py-0.5 rounded-full font-bold" style={{
                      backgroundColor: ov.health === 'green' ? '#2D8B5E' : ov.health === 'amber' ? '#D97706' : '#DC2626',
                      color: '#fff'
                    }}>{ov.health}</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 flex-1 max-w-2xl">
                  {ov.score_breakdown.map((d, i) => (
                    <div key={i} className="text-center p-2 rounded-md bg-white/60 border" style={{ borderColor: '#E5E1DC' }}>
                      <p className="text-[8px] uppercase font-medium" style={{ color: '#9CA3AF' }}>{d.dimension}</p>
                      <p className="text-sm font-bold" style={{ color: (typeof d.score === 'number' && d.score >= 8) ? '#2D8B5E' : (typeof d.score === 'number' && d.score >= 6) ? '#D97706' : (typeof d.score === 'number') ? '#DC2626' : '#9CA3AF' }}>{d.score}</p>
                      <p className="text-[8px]" style={{ color: '#6B7280' }}>{d.weight}% · {d.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {ov.health === 'insufficient_data' && (
            <div className="rounded-xl border p-4 text-center" style={{ borderColor: '#E5E1DC', backgroundColor: '#F7F5F2' }} data-testid="insufficient-data-notice">
              <p className="text-sm font-medium" style={{ color: '#6B7280' }}>
                {ov.outlet_type === 'cost_centre' ? 'Cost centre — no revenue target, no performance score' : 'Insufficient data for performance scoring'}
              </p>
              <p className="text-[11px] mt-1" style={{ color: '#9CA3AF' }}>
                {ov.outlet_type === 'cost_centre' ? 'Track monthly costs + allocation ratios below.' : 'Enter daily sales, F&B costs, or reviews to populate the score.'}
              </p>
            </div>
          )}

          {/* Cost Centre Summary Card (replaces revenue KPIs for cost centres) */}
          {isCC && costCentre && (
            <div className="bg-white rounded-xl border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }} data-testid="cost-centre-summary">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#6B7280' }}>
                    {costCentre.cost_tier === 'hq' ? 'Tier 3 · HQ Overhead' : 'Tier 2 · Shared Property Cost'}
                  </p>
                  <p className="text-2xl font-bold" style={{ color: '#D97706' }}>{fC(costCentre.total_monthly)}<span className="text-sm font-normal ml-1" style={{ color: '#9CA3AF' }}>/month</span></p>
                  <p className="text-[11px]" style={{ color: '#9CA3AF' }}>{costCentre.period_label}</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 flex-1 max-w-2xl">
                  <div className="bg-[#F7F5F2] rounded-md p-2">
                    <p className="text-[9px] uppercase" style={{ color: '#6B7280' }}>Labour</p>
                    <p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{fC(costCentre.labour)}</p>
                    <p className="text-[9px]" style={{ color: '#9CA3AF' }}>{costCentre.labour_headcount} people</p>
                  </div>
                  <div className="bg-[#F7F5F2] rounded-md p-2">
                    <p className="text-[9px] uppercase" style={{ color: '#6B7280' }}>Common Area</p>
                    <p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{fC(costCentre.common_area_monthly)}</p>
                  </div>
                  <div className="bg-[#F7F5F2] rounded-md p-2">
                    <p className="text-[9px] uppercase" style={{ color: '#6B7280' }}>Payables MTD</p>
                    <p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{fC(costCentre.payables_mtd)}</p>
                  </div>
                  <div className="bg-[#F7F5F2] rounded-md p-2">
                    <p className="text-[9px] uppercase" style={{ color: '#6B7280' }}>Claims (Paid)</p>
                    <p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{fC(costCentre.expense_claims_mtd)}</p>
                  </div>
                </div>
              </div>

              {/* Allocation / Downstream Impact — editable for CEO/leader */}
              <AllocationEditor
                encodedName={encodedName}
                costCentre={costCentre}
                canEdit={user?.role === 'ceo' || user?.role === 'leader'}
                divisionId={ov.division_id}
                onSaved={async () => {
                  try {
                    const ccRes = await api.get(`/outlet-intel/${encodedName}/cost-centre-summary`);
                    setCostCentre(ccRes.data);
                  } catch (e) { /* non-blocking */ }
                }}
              />

              {/* Labour breakdown (CEO/leader only) */}
              {costCentre.labour_breakdown && costCentre.labour_breakdown.length > 0 && (
                <div className="border-t pt-3" style={{ borderColor: '#F3F4F6' }}>
                  <p className="text-[10px] uppercase tracking-wider font-medium mb-1.5" style={{ color: '#6B7280' }}>Labour Breakdown</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b" style={{ borderColor: '#E5E1DC' }}>
                          <th className="text-left py-1.5 font-medium" style={{ color: '#6B7280' }}>Name</th>
                          <th className="text-left py-1.5 font-medium" style={{ color: '#6B7280' }}>Designation</th>
                          <th className="text-right py-1.5 font-medium" style={{ color: '#6B7280' }}>Salary/mo</th>
                          <th className="text-right py-1.5 font-medium" style={{ color: '#6B7280' }}>% here</th>
                          <th className="text-right py-1.5 font-medium" style={{ color: '#6B7280' }}>Cost/mo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {costCentre.labour_breakdown.map((b, i) => (
                          <tr key={i} className="border-b last:border-0" style={{ borderColor: '#F3F4F6' }}>
                            <td className="py-1.5" style={{ color: '#1B2A4A' }}>{b.name}</td>
                            <td className="py-1.5" style={{ color: '#6B7280' }}>{b.designation}</td>
                            <td className="py-1.5 text-right" style={{ color: '#1B2A4A' }}>{fC(b.salary_monthly)}</td>
                            <td className="py-1.5 text-right" style={{ color: '#9CA3AF' }}>{b.pct_here}%</td>
                            <td className="py-1.5 text-right font-medium" style={{ color: '#D97706' }}>{fC(b.monthly_cost)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* KPI Cards - hidden for cost centres (they have no revenue) */}
          {!isCC && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
            {(() => {
              // Pace calc — day-in-quarter × quarter_target vs actual qtd
              const now = new Date();
              const month = now.getMonth(); // 0..11
              const qIdx = Math.floor(((month + 9) % 12) / 3); // Apr=Q1 → idx 0
              const qStartMonth = [3, 6, 9, 0][qIdx];
              const qStartYear = month >= 3 ? now.getFullYear() : now.getFullYear() - 1;
              const qStart = new Date(qIdx === 3 ? qStartYear + 1 : qStartYear, qStartMonth, 1);
              const qEnd = new Date(qStart);
              qEnd.setMonth(qEnd.getMonth() + 3);
              const daysInQuarter = Math.round((qEnd - qStart) / 86400000);
              const daysElapsed = Math.max(1, Math.min(daysInQuarter, Math.floor((now - qStart) / 86400000) + 1));
              const paceActual = rv.qtd || 0;
              const paceTarget = rv.quarter_target || 0;
              return [
                { label: ov.b2b?.enabled ? 'Realised YTD' : 'Revenue YTD', value: rv.ytd, sub: `Target: ${fC(rv.annual_target)}`, accent: '#378ADD' },
                { label: ov.b2b?.enabled ? 'Realised Quarter' : 'This Quarter', value: rv.qtd, sub: `Target: ${fC(rv.quarter_target)}`, accent: '#2D8B5E' },
                { label: ov.b2b?.enabled ? 'Realised Month' : 'This Month', value: rv.mtd, sub: rv.week > 0 ? `+${fC(rv.week)} this week` : null, accent: '#D97706' },
                {
                  label: 'Pace (Q)',
                  accent: paceTarget > 0 && paceActual >= (daysElapsed / daysInQuarter) * paceTarget ? '#2D8B5E' : '#D4726A',
                  raw: true,
                  customNode: paceTarget > 0 ? (
                    <div>
                      <PaceIndicator actual={paceActual} target={paceTarget} daysElapsed={daysElapsed} daysInPeriod={daysInQuarter} size="sm" />
                      <p className="text-[10px] mt-1.5" style={{ color: '#6B7280' }}>Exp {fC((daysElapsed / daysInQuarter) * paceTarget)} by day {daysElapsed}/{daysInQuarter}</p>
                    </div>
                  ) : <span className="text-[11px]" style={{ color: '#9CA3AF' }}>No target</span>,
                },
                { label: 'Headcount', value: ov.headcount, sub: `${ov.departments.length} departments`, accent: '#7C3AED', raw: true },
              ];
            })().map((kpi, i) => (
              <div key={i} className="bg-white rounded-xl p-3 border" style={{ borderColor: '#E5E1DC' }}>
                <div className="w-6 h-[3px] rounded-sm mb-2" style={{ backgroundColor: kpi.accent }} />
                <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#9CA3AF' }}>{kpi.label}</p>
                {kpi.customNode ? (
                  <div className="mt-1">{kpi.customNode}</div>
                ) : (
                  <>
                    <p className="text-xl sm:text-2xl font-bold mt-1" style={{ color: '#1B2A4A' }}>{kpi.raw ? kpi.value : fC(kpi.value)}</p>
                    {kpi.sub && <p className="text-[10px] mt-0.5" style={{ color: '#6B7280' }}>{kpi.sub}</p>}
                  </>
                )}
              </div>
            ))}
          </div>
          )}

          {/* B2B Contracts + Realised Revenue panel */}
          {!isCC && ov.b2b?.enabled && <B2BRevenuePanel outletName={outletName} b2b={ov.b2b} onChange={refreshOverview} />}

          {/* Channel Mix — shows Online / Store / Swiggy / Zomato / Delivery split when available.
              Renders automatically on any outlet whose daily sales use sub_amounts (primarily TDB). */}
          {!isCC && <ChannelMixWidget divisionId={ov.division_id} outletName={outletName} />}

          {/* Margin Cards - only for F&B/hospitality, hidden for cost centres & services */}
          {!isCC && !isServices && (ov.fb_cost_pct > 0 || ov.labour_cost_pct > 0) && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <MetricCard label="F&B Cost %" value={`${ov.fb_cost_pct}%`} target="<30%" status={ov.fb_cost_pct < 30 ? 'green' : ov.fb_cost_pct < 35 ? 'amber' : 'red'} />
              <MetricCard label="Labour Cost %" value={ov.labour_cost_pct > 0 ? `${ov.labour_cost_pct}%` : '—'} target="<28%" status={ov.labour_cost_pct === 0 ? 'neutral' : ov.labour_cost_pct < 25 ? 'green' : ov.labour_cost_pct < 30 ? 'amber' : 'red'} />
              {ov.contribution_monthly != null && (
                <MetricCard label="Monthly Contribution" value={fC(ov.contribution_monthly)} target="After food+labour" status={ov.contribution_monthly > 0 ? 'green' : 'red'} />
              )}
              {ov.total_salary_monthly != null && (
                <MetricCard label="Salary Bill" value={ov.total_salary_monthly > 0 ? fC(ov.total_salary_monthly) : '—'} target="per month" status="neutral" />
              )}
            </div>
          )}

          {/* Department Breakdown */}
          {ov.departments.length > 0 && (
            <div className="bg-white rounded-xl border p-3" style={{ borderColor: '#E5E1DC' }}>
              <p className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>Department Breakdown</p>
              <div className="flex flex-wrap gap-1.5">
                {ov.departments.map((d, i) => (
                  <span key={i} className="text-[11px] px-2 py-1 rounded-full" style={{ backgroundColor: '#F7F5F2', color: '#1B2A4A' }}>
                    {d.name} <strong>({d.count})</strong>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Quick Revenue Trend (mini chart) — bars with date + value labels, matching Section 2 */}
          {revenue && revenue.daily_trend.length > 0 && (
            <div className="bg-white rounded-xl border p-3" style={{ borderColor: '#E5E1DC' }} data-testid="overview-daily-trend">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>Daily Revenue Trend (30d)</p>
                <p className="text-[10px]" style={{ color: '#9CA3AF' }}>{revenue.entry_count} entries · Total: {fC(revenue.total)}</p>
              </div>
              <div style={{ height: 180 }}>
                <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 1, height: 1 }}>
                  <BarChart data={revenue.daily_trend} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                    <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={d => d.slice(5)} interval={0} />
                    <YAxis tick={{ fontSize: 9 }} tickFormatter={v => fC(v)} width={42} />
                    <Tooltip formatter={v => [fC(v), 'Revenue']} labelFormatter={l => l} />
                    <Bar dataKey="amount" radius={[3, 3, 0, 0]}>
                      {revenue.daily_trend.map((e, i) => (
                        <Cell key={i} fill={i === revenue.daily_trend.length - 1 ? '#2A7B88' : '#2A7B8840'} />
                      ))}
                      <LabelList
                        dataKey="amount"
                        position="top"
                        style={{ fontSize: 8, fill: '#1B2A4A', fontWeight: 500 }}
                        formatter={v => (v > 0 ? fC(v) : '')}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Multi-year Revenue Trend — current FY vs target + historical placeholders */}
          {!isCC && rv.annual_target > 0 && (
            <RevenueTrendWidget
              outletName={outletName}
              subjectLabel={outletName}
              isCEO={user?.role === 'ceo'}
            />
          )}
        </div>
      )}

      {/* === SECTION 2: REVENUE === */}
      {activeSection === 'revenue' && (
        isServices
          ? <MonthlyBillingTab outletName={outletName} onChange={refreshOverview} />
          : <RevenueSection encodedName={encodedName} />
      )}

      {/* === SECTION 3: COST === */}
      {activeSection === 'cost' && (
        <div className="bg-white rounded-xl border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Cost Intelligence</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <MetricCard label="F&B Cost %" value={`${ov.fb_cost_pct}%`} target="Target: <30%" status={ov.fb_cost_pct < 30 ? 'green' : ov.fb_cost_pct < 35 ? 'amber' : 'red'} />
            <MetricCard label="Labour %" value={ov.labour_cost_pct > 0 ? `${ov.labour_cost_pct}%` : 'Pending salary data'} target="Target: <28%" status={ov.labour_cost_pct === 0 ? 'neutral' : ov.labour_cost_pct < 28 ? 'green' : 'amber'} />
            {ov.contribution_monthly != null && <MetricCard label="Monthly Contribution" value={fC(ov.contribution_monthly)} target="Revenue - Food - Labour" status={ov.contribution_monthly > 0 ? 'green' : 'red'} />}
          </div>
          <p className="text-[11px] italic" style={{ color: '#9CA3AF' }}>Detailed kitchen-level consumption, NC breakdown, and theoretical vs actual variance available in the Cost tab on the ME division dashboard.</p>
        </div>
      )}

      {/* === SECTION 4: PEOPLE === */}
      {activeSection === 'people' && people && (
        <div className="space-y-3" data-testid="section-people-content">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>People at {outletName.split('(')[0].trim()} ({people.total})</p>
          </div>
          {/* Department chips */}
          <div className="flex flex-wrap gap-1.5">
            {ov.departments.map((d, i) => (
              <span key={i} className="text-[11px] px-2 py-1 rounded-full" style={{ backgroundColor: '#F7F5F2', color: '#1B2A4A' }}>
                {d.name} ({d.count})
              </span>
            ))}
          </div>
          {/* People cards (mobile) / table (desktop) */}
          <div className="space-y-2 sm:hidden">
            {people.people.map(p => (
              <div key={p.id} onClick={() => navigate(`/people/${p.id}`)} className="bg-white rounded-lg border p-3 cursor-pointer active:bg-gray-50" style={{ borderColor: '#E5E1DC' }}>
                <p className="text-sm font-medium" style={{ color: '#1B2A4A' }}>{p.name}</p>
                <p className="text-[11px]" style={{ color: '#6B7280' }}>{p.designation} · {p.department}</p>
                {p.doj && <p className="text-[10px] mt-0.5" style={{ color: '#9CA3AF' }}>Joined: {p.doj}</p>}
              </div>
            ))}
          </div>
          <div className="hidden sm:block bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
            <table className="w-full text-[12px]">
              <thead><tr style={{ backgroundColor: '#F7F5F2' }}>
                <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Name</th>
                <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Designation</th>
                <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Department</th>
                <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>DOJ</th>
                <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Reports To</th>
              </tr></thead>
              <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                {people.people.map(p => (
                  <tr key={p.id} onClick={() => navigate(`/people/${p.id}`)} className="hover:bg-[#FAFAF8] cursor-pointer">
                    <td className="px-3 py-2 font-medium" style={{ color: '#1B2A4A' }}>{p.name}</td>
                    <td className="px-3 py-2" style={{ color: '#6B7280' }}>{p.designation || '—'}</td>
                    <td className="px-3 py-2" style={{ color: '#6B7280' }}>{p.department || '—'}</td>
                    <td className="px-3 py-2" style={{ color: '#9CA3AF' }}>{p.doj || '—'}</td>
                    <td className="px-3 py-2" style={{ color: '#6B7280' }}>{p.reporting_to || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* === SECTION 5B: REVIEWS & REPUTATION === */}
      {activeSection === 'reviews' && (
        <ReviewsSection encodedName={encodedName} outletName={outletName} />
      )}

      {/* === SECTION 18: PREVENTIVE MAINTENANCE === */}
      {activeSection === 'pm' && (
        <PMSchedulesSection encodedName={encodedName} outletName={outletName} userRole={user?.role} />
      )}

      {/* === SECTION 9: OPERATIONS & INCIDENTS === */}
      {activeSection === 'operations' && (
        <OperationsSection encodedName={encodedName} outletName={outletName} userRole={user?.role} />
      )}

      {/* === BAR VARIANCE (P2) === */}
      {activeSection === 'bar' && (
        <BarVarianceSection encodedName={encodedName} outletName={outletName} userRole={user?.role} />
      )}

      {/* === SECTION 11: COMPLIANCE === */}
      {activeSection === 'compliance' && (
        <ComplianceSection encodedName={encodedName} />
      )}

      {/* === SECTION 14: ASSETS === */}
      {activeSection === 'assets' && (
        <AssetsSection encodedName={encodedName} />
      )}

      {/* === SECTION 18: WORK ORDERS === */}
      {activeSection === 'workorders' && (
        <WorkOrdersSection encodedName={encodedName} />
      )}

      {/* === SECTION 15: CROCKERY & LINEN === */}
      {activeSection === 'crockery' && (
        <CrockeryLinenSection encodedName={encodedName} />
      )}

      {/* === SECTION 16: ACCOMMODATION INVENTORY === */}
      {activeSection === 'accommodation' && (
        <AccommodationSection encodedName={encodedName} />
      )}

      {/* === SECTION 17: SOPs === */}
      {activeSection === 'sops' && (
        <SopsSection encodedName={encodedName} />
      )}

      {/* === SECTION 20: COMMON AREAS === */}
      {activeSection === 'common' && (
        <CommonAreasSection encodedName={encodedName} />
      )}

      {/* === SECTION 12: OUTLET PROFILE === */}
      {activeSection === 'profile' && profile && (
        <div className="space-y-3" data-testid="section-profile-content">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Outlet Profile</p>
            <div className="flex items-center gap-2">
              <button
                onClick={async () => {
                  // Resolve the Client dossier by outlet/brand name, scoped to this division.
                  // Fuzzy match: exact > either-contains the other (case-insensitive).
                  try {
                    const r = await api.get(`/clients-registry?division_id=${ov.division_id}`);
                    const lower = outletName.toLowerCase().trim();
                    const list = r.data || [];
                    const exact = list.find(c =>
                      (c.client_name || '').toLowerCase().trim() === lower ||
                      (c.entity || '').toLowerCase().trim() === lower,
                    );
                    const fuzzy = exact || list.find(c => {
                      const n = (c.client_name || '').toLowerCase().trim();
                      return n && (n.includes(lower) || lower.includes(n));
                    });
                    if (fuzzy) {
                      navigate(`/client/${fuzzy.id}`);
                    } else {
                      const createNow = window.confirm(`No client dossier found for "${outletName}". Create one now?`);
                      if (createNow) {
                        const cr = await api.post('/clients-registry', { client_name: outletName, division_id: ov.division_id });
                        navigate(`/client/${cr.data.id}`);
                      }
                    }
                  } catch {
                    alert('Could not load client dossier');
                  }
                }}
                className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium border hover:bg-[#F7F5F2]"
                style={{ borderColor: '#2A7B88', color: '#2A7B88' }}
                data-testid="open-client-file-btn"
              >
                <Building2 size={12} /> Open Client File
              </button>
              {!editProfile ? (
                <button onClick={() => { setProfileForm({...profile}); setEditProfile(true); }} className="flex items-center gap-1 text-xs" style={{ color: '#2A7B88' }}><Pencil size={12} /> Edit</button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={saveProfile} className="px-3 py-1 text-xs font-medium text-white rounded-md" style={{ backgroundColor: '#2A7B88' }}>Save</button>
                  <button onClick={() => setEditProfile(false)} className="px-3 py-1 text-xs rounded-md border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E5E1DC' }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { key: 'brand_name', label: 'Brand Name', icon: Building2 },
                { key: 'legal_name', label: 'Legal Entity Name' },
                { key: 'address', label: 'Full Address', wide: true },
                { key: 'city', label: 'City' },
                { key: 'state', label: 'State' },
                { key: 'pin_code', label: 'PIN Code' },
                { key: 'phone', label: 'Phone', icon: Phone },
                { key: 'email', label: 'Email' },
                { key: 'website', label: 'Website', icon: Globe },
                { key: 'google_maps_url', label: 'Google Maps URL' },
                { key: 'zomato_url', label: 'Zomato Page' },
                { key: 'swiggy_url', label: 'Swiggy Page' },
                { key: 'instagram', label: 'Instagram', icon: Instagram },
                { key: 'facebook', label: 'Facebook' },
                { key: 'seating_capacity', label: 'Seating Capacity (covers)', type: 'number' },
                { key: 'rooms', label: 'Rooms', type: 'number' },
                { key: 'parking', label: 'Parking' },
                { key: 'year_established', label: 'Year Established' },
                { key: 'property_type', label: 'Property Type' },
                { key: 'lease_expiry', label: 'Lease Expiry', type: 'date' },
              ].map(f => (
                <div key={f.key} className={f.wide ? 'sm:col-span-2' : ''}>
                  <label className="text-[10px] font-medium uppercase tracking-wider" style={{ color: '#9CA3AF' }}>{f.label}</label>
                  {editProfile ? (
                    <input type={f.type || 'text'} value={profileForm[f.key] || ''} onChange={e => setProfileForm(p => ({...p, [f.key]: e.target.value}))}
                      className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
                  ) : (
                    <p className="text-sm mt-0.5" style={{ color: profile[f.key] ? '#1B2A4A' : '#D4D4D8' }}>
                      {profile[f.key] || '—'}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, target, status }) {
  const colors = {
    green: { bg: '#ECFDF5', text: '#2D8B5E' },
    amber: { bg: '#FEF3C7', text: '#D97706' },
    red: { bg: '#FEF2F2', text: '#DC2626' },
    neutral: { bg: '#F7F5F2', text: '#6B7280' },
  };
  const c = colors[status] || colors.neutral;
  return (
    <div className="rounded-xl p-3 border" style={{ borderColor: '#E5E1DC', backgroundColor: c.bg }}>
      <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#9CA3AF' }}>{label}</p>
      <p className="text-lg font-bold mt-0.5" style={{ color: c.text }}>{value}</p>
      {target && <p className="text-[10px]" style={{ color: '#9CA3AF' }}>{target}</p>}
    </div>
  );
}

function RevenueSection({ encodedName }) {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('last_30d');

  useEffect(() => {
    api.get(`/outlet-intel/${encodedName}/revenue?period=${period}`).then(r => setData(r.data)).catch(() => {});
  }, [encodedName, period]);

  if (!data) return <p className="py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>Loading revenue...</p>;

  return (
    <div className="space-y-3" data-testid="section-revenue-content">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Revenue Intelligence</p>
        <div className="flex gap-1">
          {['last_7d', 'last_30d', 'last_90d', 'ytd'].map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              className={`px-2.5 py-1 text-[11px] rounded-full transition-all ${period === p ? 'bg-[#2A7B88] text-white' : 'text-[#6B7280] hover:bg-[#F7F5F2]'}`}>
              {p === 'last_7d' ? '7D' : p === 'last_30d' ? '30D' : p === 'last_90d' ? '90D' : 'YTD'}
            </button>
          ))}
        </div>
      </div>

      {/* Revenue total */}
      <div className="bg-white rounded-xl border p-3" style={{ borderColor: '#E5E1DC' }}>
        <p className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Total ({period.replace('last_', '').replace('d', ' days')})</p>
        <p className="text-2xl font-bold" style={{ color: '#1B2A4A' }}>{fC(data.total)}</p>
        <p className="text-[10px]" style={{ color: '#6B7280' }}>{data.entry_count} entries</p>
      </div>

      {/* Daily trend */}
      {data.daily_trend.length > 0 && (
        <div className="bg-white rounded-xl border p-3" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>Daily Revenue Trend</p>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 1, height: 1 }}>
              <BarChart data={data.daily_trend} margin={{ top: 22, right: 8, left: 0, bottom: 0 }}>
                <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={d => d.slice(5)} interval={0} />
                <YAxis tick={{ fontSize: 9 }} tickFormatter={v => fC(v)} width={42} />
                <Tooltip formatter={v => [fC(v), 'Revenue']} labelFormatter={l => l} />
                <Bar dataKey="amount" radius={[3, 3, 0, 0]}>
                  {data.daily_trend.map((e, i) => (
                    <Cell key={i} fill={i === data.daily_trend.length - 1 ? '#2A7B88' : '#2A7B8840'} />
                  ))}
                  <LabelList
                    dataKey="amount"
                    position="top"
                    style={{ fontSize: 9, fill: '#1B2A4A', fontWeight: 500 }}
                    formatter={v => (v > 0 ? fC(v) : '')}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Sub-category breakdown */}
      {data.sub_breakdown.length > 0 && (
        <div className="bg-white rounded-xl border p-3" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>Revenue by Sub-Category</p>
          <div className="space-y-1.5">
            {data.sub_breakdown.map((s, i) => {
              const pct = data.total > 0 ? (s.value / data.total * 100) : 0;
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-[11px] w-32 sm:w-48 truncate" style={{ color: '#6B7280' }}>{s.name}</span>
                  <div className="flex-1 h-2 rounded-full bg-[#F7F5F2] overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: '#2A7B88' }} />
                  </div>
                  <span className="text-[11px] font-medium w-16 text-right" style={{ color: '#1B2A4A' }}>{fC(s.value)}</span>
                  <span className="text-[10px] w-10 text-right" style={{ color: '#9CA3AF' }}>{pct.toFixed(0)}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


function ComplianceSection({ encodedName }) {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const importRef = useRef(null);

  const fetch = () => api.get(`/outlet-intel/${encodedName}/compliance`).then(r => setData(r.data)).catch(() => {});
  useEffect(() => { fetch(); }, [encodedName]);

  const save = async () => {
    if (editId) {
      await api.put(`/outlet-intel/${encodedName}/compliance/${editId}`, form);
    } else {
      await api.post(`/outlet-intel/${encodedName}/compliance`, form);
    }
    setShowForm(false); setForm({}); setEditId(null); fetch();
  };
  const startEdit = (e) => { setEditId(e.id); setForm({...e}); setShowForm(true); };
  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    await api.delete(`/outlet-intel/${encodedName}/compliance/${id}`); fetch();
  };
  const handleImport = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const r = await api.post(`/outlet-intel/${encodedName}/compliance/import-csv`, fd); alert(`Imported ${r.data.imported} records`); fetch(); } catch {}
    ev.target.value = '';
  };

  if (!data) return <p className="py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>Loading...</p>;

  const statusColors = {
    'Valid': { bg: '#ECFDF5', text: '#2D8B5E' },
    'Expiring Soon': { bg: '#FEF2F2', text: '#DC2626' },
    'Expiring (90d)': { bg: '#FEF3C7', text: '#D97706' },
    'Expired': { bg: '#FEE2E2', text: '#991B1B' },
    'Applied for Renewal': { bg: '#EFF6FF', text: '#2563EB' },
    'Not Applicable': { bg: '#F3F4F6', text: '#6B7280' },
  };
  const owners = Array.from(new Set((data.entries || []).map(e => e.owner).filter(Boolean))).sort();
  const entries = (data.entries || []).filter(e => {
    if (statusFilter && e.status !== statusFilter) return false;
    if (ownerFilter && e.owner !== ownerFilter) return false;
    return true;
  });

  return (
    <div className="space-y-3" data-testid="section-compliance">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Compliance & Licenses ({data.total})</p>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-2 py-1 text-[11px] rounded-md border" style={{ borderColor: '#E5E1DC' }} data-testid="compliance-status-filter">
            <option value="">All statuses</option>
            {Object.keys(statusColors).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {owners.length > 0 && (
            <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)} className="px-2 py-1 text-[11px] rounded-md border" style={{ borderColor: '#E5E1DC' }} data-testid="compliance-owner-filter">
              <option value="">All owners</option>
              {owners.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          )}
          {(statusFilter || ownerFilter) && (
            <button onClick={() => { setStatusFilter(''); setOwnerFilter(''); }} className="text-[10px] px-2 py-1 rounded hover:bg-gray-100" style={{ color: '#D4726A' }}>Clear</button>
          )}
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <a href={`${API_URL}/api/outlet-intel/${encodedName}/compliance/export-csv?token=${localStorage.getItem('access_token')}`}
            className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
            <Download size={11} /> Export
          </a>
          <button onClick={() => importRef.current?.click()} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
            <Upload size={11} /> Import
          </button>
          <input ref={importRef} type="file" accept=".csv" className="hidden" onChange={handleImport} />
          <button onClick={() => { setShowForm(true); setEditId(null); setForm({}); }} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }}>
            <Plus size={12} /> Add License
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E5E1DC' }}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>Document Type</label>
              <select value={form.document_type || ''} onChange={e => setForm(f => ({...f, document_type: e.target.value}))} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
                <option value="">Select...</option>
                {(data.compliance_types || []).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <FI l="License Number" v={form.license_number} o={v => setForm(f => ({...f, license_number: v}))} />
            <FI l="Issuing Authority" v={form.issuing_authority} o={v => setForm(f => ({...f, issuing_authority: v}))} />
            <FI l="Issue Date" v={form.issue_date} o={v => setForm(f => ({...f, issue_date: v}))} t="date" />
            <FI l="Expiry Date" v={form.expiry_date} o={v => setForm(f => ({...f, expiry_date: v}))} t="date" />
            <FI l="Renewal Cost" v={form.renewal_cost} o={v => setForm(f => ({...f, renewal_cost: v}))} t="number" />
            <FI l="Owner" v={form.owner} o={v => setForm(f => ({...f, owner: v}))} />
            <FI l="Last Checked" v={form.last_checked} o={v => setForm(f => ({...f, last_checked: v}))} t="date" />
            <FI l="Next Check Due" v={form.next_check_due} o={v => setForm(f => ({...f, next_check_due: v}))} t="date" />
            <div className="sm:col-span-3"><FI l="Notes" v={form.notes} o={v => setForm(f => ({...f, notes: v}))} /></div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={save} className="px-4 py-2 text-xs font-medium text-white rounded-md" style={{ backgroundColor: '#2A7B88' }}>Save</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-xs rounded-md border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          </div>
        </div>
      )}

      {entries.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-xl border" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-sm" style={{ color: '#9CA3AF' }}>{data.entries.length === 0 ? 'No compliance records yet. Add licenses and certificates to track.' : 'No records match the current filters.'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map(e => {
            const sc = statusColors[e.computed_status] || statusColors['Valid'];
            return (
              <div key={e.id} className="bg-white rounded-xl border p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4" style={{ borderColor: '#E5E1DC' }}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium" style={{ color: '#1B2A4A' }}>{e.document_type}</p>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: sc.bg, color: sc.text }}>{e.computed_status}</span>
                  </div>
                  <p className="text-[11px] mt-0.5" style={{ color: '#6B7280' }}>
                    {e.license_number && `#${e.license_number} · `}{e.issuing_authority && `${e.issuing_authority} · `}
                    {e.issue_date && `Issued: ${e.issue_date}`}{e.expiry_date && ` · Expires: ${e.expiry_date}`}
                  </p>
                  {e.notes && <p className="text-[10px] mt-0.5 italic" style={{ color: '#9CA3AF' }}>{e.notes}</p>}
                </div>
                <div className="flex items-start gap-2 shrink-0">
                  {e.photo_url && <a href={fixCloudinaryDocUrl(e.photo_url)} target="_blank" rel="noreferrer" className="text-[10px] px-2 py-1 rounded bg-blue-50 text-blue-700">View Doc</a>}
                  <label className="text-[10px] px-2 py-1 rounded hover:bg-[#F7F5F2] cursor-pointer" style={{ color: '#2A7B88' }}>
                    Upload
                    <input type="file" accept="image/*,.pdf" className="hidden" onChange={async (ev) => {
                      const file = ev.target.files?.[0]; if (!file) return;
                      const fd = new FormData(); fd.append('file', file); fd.append('folder', 'compliance');
                      try {
                        // Use /upload-doc (resource_type=auto) so PDFs go to
                        // Cloudinary's raw/ path. The /upload endpoint forces
                        // resource_type=image which makes PDFs upload but
                        // become un-viewable (Cloudinary blocks PDF delivery
                        // from image/ paths). Dipali · 11-May-2026.
                        const r = await api.post('/cloudinary/upload-doc', fd);
                        await api.put(`/outlet-intel/${encodedName}/compliance/${e.id}`, { photo_url: r.data.url });
                        fetch();
                      } catch (err) { alert('Upload failed: ' + (err?.response?.data?.detail || err.message)); }
                      ev.target.value = '';
                    }} />
                  </label>
                  <button onClick={() => startEdit(e)} className="text-[10px] px-2 py-1 rounded hover:bg-[#F7F5F2]" style={{ color: '#2A7B88' }}>Edit</button>
                  <button onClick={() => del(e.id)} className="text-[10px] px-2 py-1 rounded hover:bg-red-50" style={{ color: '#D4726A' }}>Del</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AssetsSection({ encodedName }) {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ condition: 'Good', quantity: 1 });
  const [editId, setEditId] = useState(null);
  const [catFilter, setCatFilter] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [roomFilter, setRoomFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const importRef = useRef(null);

  const fetch = () => {
    let url = `/outlet-intel/${encodedName}/assets`;
    if (catFilter) url += `?category=${catFilter}`;
    api.get(url).then(r => setData(r.data)).catch(() => {});
  };
  useEffect(() => { fetch(); }, [encodedName, catFilter]);

  const save = async () => {
    try {
      if (editId) {
        await api.put(`/outlet-intel/${encodedName}/assets/${editId}`, form);
      } else {
        await api.post(`/outlet-intel/${encodedName}/assets`, form);
      }
      setShowForm(false); setForm({ condition: 'Good', quantity: 1 }); setEditId(null); fetch();
    } catch (e) {
      // 11-May-2026 · Surface backend errors instead of silently swallowing.
      // Dhiraj's report: "edit button not working on some assets" — root
      // cause was an unhandled API error that left the form stuck open.
      alert('Save failed: ' + (e?.response?.data?.detail || e?.message || 'unknown error'));
    }
  };
  const startEdit = (a) => {
    // Ensure quantity has a sensible default if the asset predates the field.
    setEditId(a.id);
    setForm({ ...a, quantity: a.quantity || 1 });
    setShowForm(true);
  };
  const del = async (id) => {
    if (!window.confirm('Delete asset?')) return;
    await api.delete(`/outlet-intel/${encodedName}/assets/${id}`); fetch();
  };
  const handleImport = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const r = await api.post(`/outlet-intel/${encodedName}/assets/import-csv`, fd); alert(`Imported ${r.data.imported} assets`); fetch(); } catch {}
    ev.target.value = '';
  };

  if (!data) return <p className="py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>Loading...</p>;

  const condColors = {
    'Excellent': '#2D8B5E', 'Good': '#2A7B88', 'Fair': '#D97706',
    'Needs Repair': '#DC2626', 'Under Repair': '#EA580C', 'Condemned': '#991B1B',
  };

  // Derive Room / Area dropdown options from the loaded asset list.
  // 11-May-2026 · Dhiraj asked for filter-by-Room-Name and filter-by-Area
  // so we can narrow a 1,000-row asset register fast.
  const allRooms = Array.from(new Set((data.assets || []).map(a => a.room_name).filter(Boolean))).sort();
  const allAreas = Array.from(new Set((data.assets || []).map(a => a.area).filter(Boolean))).sort();
  const q = (searchQ || '').trim().toLowerCase();
  const visibleAssets = (data.assets || []).filter(a => {
    if (q && !`${a.asset_name || ''} ${a.brand || ''} ${a.model || ''} ${a.asset_tag || ''} ${a.location_detail || ''}`.toLowerCase().includes(q)) return false;
    if (roomFilter && a.room_name !== roomFilter) return false;
    if (areaFilter && a.area !== areaFilter) return false;
    return true;
  });

  return (
    <div className="space-y-3" data-testid="section-assets">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Assets ({data.total})</p>
          <p className="text-[10px]" style={{ color: '#9CA3AF' }}>Total value: {fC(data.total_value)}{data.needs_attention > 0 && ` · ${data.needs_attention} need attention`}</p>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <input
            type="search"
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
            placeholder="Search name / brand / tag / location…"
            className="px-2 py-1 text-[11px] rounded-md border w-48"
            style={{ borderColor: '#E5E1DC' }}
            data-testid="asset-search"
          />
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="px-2 py-1 text-[11px] rounded-md border" style={{ borderColor: '#E5E1DC' }} data-testid="asset-cat-filter">
            <option value="">All Categories</option>
            {(data.categories || []).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={roomFilter} onChange={e => setRoomFilter(e.target.value)} className="px-2 py-1 text-[11px] rounded-md border" style={{ borderColor: '#E5E1DC' }} data-testid="asset-room-filter">
            <option value="">All Rooms</option>
            {allRooms.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <select value={areaFilter} onChange={e => setAreaFilter(e.target.value)} className="px-2 py-1 text-[11px] rounded-md border" style={{ borderColor: '#E5E1DC' }} data-testid="asset-area-filter">
            <option value="">All Areas</option>
            {allAreas.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <a href={`${API_URL}/api/outlet-intel/${encodedName}/assets/export-csv?token=${localStorage.getItem('access_token')}`}
            className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
            <Download size={11} /> Export
          </a>
          <button onClick={() => importRef.current?.click()} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
            <Upload size={11} /> Import
          </button>
          <input ref={importRef} type="file" accept=".csv" className="hidden" onChange={handleImport} />
          <button onClick={() => { setShowForm(true); setEditId(null); setForm({ condition: 'Good', quantity: 1 }); }} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }}>
            <Plus size={12} /> Add Asset
          </button>
        </div>
      </div>

      {/* Depreciation summary — straight-line, feeds Tier 1 Other Direct Costs in P&L */}
      {(data.total_depreciation_monthly > 0 || data.total_book_value > 0) && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="asset-depreciation-summary">
          <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}>
            <p className="text-[9px] uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Gross Value</p>
            <p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{fC(data.total_value)}</p>
          </div>
          <div className="bg-[#FFFBEB] rounded-lg border p-2.5" style={{ borderColor: '#D9770640' }}>
            <p className="text-[9px] uppercase tracking-wide font-semibold" style={{ color: '#D97706' }}>Monthly Depreciation</p>
            <p className="text-lg font-bold" style={{ color: '#D97706' }}>{fC(data.total_depreciation_monthly)}</p>
            <p className="text-[9px]" style={{ color: '#9CA3AF' }}>Charged to Other Direct Costs</p>
          </div>
          <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}>
            <p className="text-[9px] uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Accumulated Dep.</p>
            <p className="text-lg font-bold" style={{ color: '#6B7280' }}>{fC(data.total_accumulated_depreciation)}</p>
          </div>
          <div className="bg-[#ECFDF5] rounded-lg border p-2.5" style={{ borderColor: '#10B98140' }}>
            <p className="text-[9px] uppercase tracking-wide font-semibold" style={{ color: '#2D8B5E' }}>Book Value</p>
            <p className="text-lg font-bold" style={{ color: '#2D8B5E' }}>{fC(data.total_book_value)}</p>
          </div>
        </div>
      )}

      {showForm && (
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E5E1DC' }}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <FI l="Asset Name" v={form.asset_name} o={v => setForm(f => ({...f, asset_name: v}))} />
            <div>
              <label className="text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>Category</label>
              <select value={form.category || 'Other'} onChange={e => setForm(f => ({...f, category: e.target.value}))} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
                {(data.categories || []).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <FI l="Asset Tag / ID" v={form.asset_tag} o={v => setForm(f => ({...f, asset_tag: v}))} />
            <FI l="Quantity" v={form.quantity} o={v => setForm(f => ({...f, quantity: v}))} t="number" />
            <FI l="Room Name" v={form.room_name} o={v => setForm(f => ({...f, room_name: v}))} />
            <FI l="Area" v={form.area} o={v => setForm(f => ({...f, area: v}))} />
            <FI l="Brand" v={form.brand} o={v => setForm(f => ({...f, brand: v}))} />
            <FI l="Model" v={form.model} o={v => setForm(f => ({...f, model: v}))} />
            <FI l="Location Detail" v={form.location_detail} o={v => setForm(f => ({...f, location_detail: v}))} />
            <FI l="Purchase Date" v={form.purchase_date} o={v => setForm(f => ({...f, purchase_date: v}))} t="date" />
            <FI l="Purchase Cost" v={form.purchase_cost} o={v => setForm(f => ({...f, purchase_cost: v}))} t="number" />
            <FI l="Warranty Expiry" v={form.warranty_expiry} o={v => setForm(f => ({...f, warranty_expiry: v}))} t="date" />
            <div>
              <label className="text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>Condition</label>
              <select value={form.condition || 'Good'} onChange={e => setForm(f => ({...f, condition: e.target.value}))} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
                {['Excellent','Good','Fair','Needs Repair','Under Repair','Condemned'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <FI l="Next Maintenance" v={form.next_maintenance} o={v => setForm(f => ({...f, next_maintenance: v}))} t="date" />
            <FI l="Owner" v={form.owner} o={v => setForm(f => ({...f, owner: v}))} />
            <FI l="Last Checked" v={form.last_checked} o={v => setForm(f => ({...f, last_checked: v}))} t="date" />
            <FI l="Next Check Due" v={form.next_check_due} o={v => setForm(f => ({...f, next_check_due: v}))} t="date" />
            <div className="sm:col-span-3"><FI l="Notes" v={form.notes} o={v => setForm(f => ({...f, notes: v}))} /></div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={save} className="px-4 py-2 text-xs font-medium text-white rounded-md" style={{ backgroundColor: '#2A7B88' }}>Save</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-xs rounded-md border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          </div>
        </div>
      )}

      {data.assets.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-xl border" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-sm" style={{ color: '#9CA3AF' }}>No assets registered yet. Add equipment, furniture, and other assets.</p>
        </div>
      ) : visibleAssets.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-xl border" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-sm" style={{ color: '#9CA3AF' }}>No assets match the current filters. <button onClick={() => { setSearchQ(''); setRoomFilter(''); setAreaFilter(''); setCatFilter(''); }} className="text-xs underline" style={{ color: '#2A7B88' }}>Clear filters</button></p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="asset-list">
          <div className="text-[11px]" style={{ color: '#6B7280' }}>
            Showing <strong style={{ color: '#1B2A4A' }}>{visibleAssets.length}</strong> of <strong style={{ color: '#1B2A4A' }}>{data.assets.length}</strong> assets
            {(searchQ || roomFilter || areaFilter || catFilter) && <> · <button onClick={() => { setSearchQ(''); setRoomFilter(''); setAreaFilter(''); setCatFilter(''); }} className="underline" style={{ color: '#2A7B88' }}>Clear filters</button></>}
          </div>
          {visibleAssets.map(a => (
            <div key={a.id} className="bg-white rounded-xl border p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4" style={{ borderColor: '#E5E1DC' }} data-testid={`asset-row-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium" style={{ color: '#1B2A4A' }}>{a.asset_name}</p>
                  {(a.quantity && a.quantity > 1) && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: '#FEF3C7', color: '#7A4F08' }}>×{a.quantity}</span>
                  )}
                  <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F7F5F2', color: '#6B7280' }}>{a.category}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ color: condColors[a.condition] || '#6B7280' }}>{a.condition}</span>
                  {a.maintenance_overdue && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-600 font-medium">Maintenance Overdue</span>}
                  {a.warranty_expired && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Warranty Expired</span>}
                </div>
                <p className="text-[11px] mt-0.5" style={{ color: '#6B7280' }}>
                  {a.brand && `${a.brand} `}{a.model && `${a.model} · `}
                  {a.room_name && `Room: ${a.room_name} · `}{a.area && `Area: ${a.area} · `}
                  {a.location_detail && `${a.location_detail} · `}
                  {a.purchase_cost > 0 && (
                    a.quantity > 1
                      ? `Unit: ${fC(a.purchase_cost)} · Total: ${fC(a.purchase_cost * a.quantity)}`
                      : `Cost: ${fC(a.purchase_cost)}`
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {a.photo_url && <a href={a.photo_url} target="_blank" rel="noreferrer" className="text-[10px] px-2 py-1 rounded bg-blue-50 text-blue-700">Photo</a>}
                <label className="text-[10px] px-2 py-1 rounded hover:bg-[#F7F5F2] cursor-pointer" style={{ color: '#2A7B88' }}>
                  Upload
                  <input type="file" accept="image/*" className="hidden" onChange={async (ev) => {
                    const file = ev.target.files?.[0]; if (!file) return;
                    const fd = new FormData(); fd.append('file', file); fd.append('folder', 'assets');
                    try {
                      const r = await api.post('/cloudinary/upload', fd);
                      await api.put(`/outlet-intel/${encodedName}/assets/${a.id}`, { photo_url: r.data.url });
                      fetch();
                    } catch {}
                    ev.target.value = '';
                  }} />
                </label>
                <button onClick={() => startEdit(a)} className="text-[10px] px-2 py-1 rounded hover:bg-[#F7F5F2]" style={{ color: '#2A7B88' }}>Edit</button>
                <button onClick={() => del(a.id)} className="text-[10px] px-2 py-1 rounded hover:bg-red-50" style={{ color: '#D4726A' }}>Del</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WorkOrdersSection({ encodedName }) {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ priority: 'Normal', category: 'General Maintenance' });
  const [editId, setEditId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const importRef = useRef(null);

  const fetch = () => {
    let url = `/outlet-intel/${encodedName}/work-orders`;
    if (statusFilter) url += `?status=${statusFilter}`;
    api.get(url).then(r => setData(r.data)).catch(() => {});
  };
  useEffect(() => { fetch(); }, [encodedName, statusFilter]);

  const save = async () => {
    if (editId) {
      await api.put(`/outlet-intel/${encodedName}/work-orders/${editId}`, form);
    } else {
      await api.post(`/outlet-intel/${encodedName}/work-orders`, form);
    }
    setShowForm(false); setForm({ priority: 'Normal', category: 'General Maintenance' }); setEditId(null); fetch();
  };
  const startEdit = (o) => { setEditId(o.id); setForm({...o}); setShowForm(true); };
  const updateStatus = async (id, status) => {
    await api.put(`/outlet-intel/${encodedName}/work-orders/${id}`, { status });
    fetch();
  };
  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    await api.delete(`/outlet-intel/${encodedName}/work-orders/${id}`); fetch();
  };
  const handleImport = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const r = await api.post(`/outlet-intel/${encodedName}/work-orders/import-csv`, fd); alert(`Imported ${r.data.imported} work orders`); fetch(); } catch {}
    ev.target.value = '';
  };

  if (!data) return <p className="py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>Loading...</p>;

  const prioColors = { Emergency: '#991B1B', Urgent: '#DC2626', Normal: '#D97706', Planned: '#2D8B5E' };
  const statusColors = { Reported: '#DC2626', Assigned: '#D97706', 'In Progress': '#2563EB', Completed: '#2D8B5E', Verified: '#6B7280' };

  return (
    <div className="space-y-3" data-testid="section-workorders">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Work Orders ({data.total})</p>
          {data.open_count > 0 && <p className="text-[10px]" style={{ color: '#DC2626' }}>{data.open_count} open</p>}
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-2 py-1 text-[11px] rounded-md border" style={{ borderColor: '#E5E1DC' }}>
            <option value="">All Status</option>
            {(data.statuses || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <a href={`${API_URL}/api/outlet-intel/${encodedName}/work-orders/export-csv?token=${localStorage.getItem('access_token')}`}
            className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
            <Download size={11} /> Export
          </a>
          <button onClick={() => importRef.current?.click()} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
            <Upload size={11} /> Import
          </button>
          <input ref={importRef} type="file" accept=".csv" className="hidden" onChange={handleImport} />
          <button onClick={() => { setShowForm(true); setEditId(null); setForm({ priority: 'Normal', category: 'General Maintenance' }); }} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }}>
            <Plus size={12} /> New Work Order
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E5E1DC' }}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2"><FI l="Title" v={form.title} o={v => setForm(f => ({...f, title: v}))} /></div>
            <div>
              <label className="text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>Priority</label>
              <select value={form.priority} onChange={e => setForm(f => ({...f, priority: e.target.value}))} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
                {(data.priorities || []).map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>Category</label>
              <select value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
                {(data.categories || []).map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <FI l="Location Detail" v={form.location_detail} o={v => setForm(f => ({...f, location_detail: v}))} />
            <FI l="Assigned To" v={form.assigned_to} o={v => setForm(f => ({...f, assigned_to: v}))} />
            <FI l="Estimated Cost" v={form.estimated_cost} o={v => setForm(f => ({...f, estimated_cost: v}))} t="number" />
            <div className="sm:col-span-3"><FI l="Description" v={form.description} o={v => setForm(f => ({...f, description: v}))} /></div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={save} className="px-4 py-2 text-xs font-medium text-white rounded-md" style={{ backgroundColor: '#2A7B88' }}>Create</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-xs rounded-md border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          </div>
        </div>
      )}

      {data.orders.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-xl border" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-sm" style={{ color: '#9CA3AF' }}>No work orders yet. Create one when something needs fixing.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.orders.map(o => (
            <div key={o.id} className="bg-white rounded-xl border p-3" style={{ borderColor: '#E5E1DC' }}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium" style={{ color: '#1B2A4A' }}>{o.title}</p>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ color: prioColors[o.priority] || '#6B7280' }}>{o.priority}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F7F5F2', color: '#6B7280' }}>{o.category}</span>
                  </div>
                  <p className="text-[11px] mt-0.5" style={{ color: '#6B7280' }}>
                    Reported: {o.reported_date} by {o.reported_by}{o.assigned_to && ` · Assigned: ${o.assigned_to}`}
                    {o.estimated_cost > 0 && ` · Est: ${fC(o.estimated_cost)}`}
                  </p>
                  {o.description && <p className="text-[10px] mt-0.5" style={{ color: '#9CA3AF' }}>{o.description}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {o.photo_url && <a href={o.photo_url} target="_blank" rel="noreferrer" className="text-[10px] px-2 py-1 rounded bg-blue-50 text-blue-700">Photo</a>}
                  <label className="text-[10px] px-2 py-1 rounded hover:bg-[#F7F5F2] cursor-pointer" style={{ color: '#2A7B88' }}>
                    Photo
                    <input type="file" accept="image/*" className="hidden" onChange={async (ev) => {
                      const file = ev.target.files?.[0]; if (!file) return;
                      const fd = new FormData(); fd.append('file', file); fd.append('folder', 'workorders');
                      try {
                        const r = await api.post('/cloudinary/upload', fd);
                        await api.put(`/outlet-intel/${encodedName}/work-orders/${o.id}`, { photo_url: r.data.url });
                        fetch();
                      } catch {}
                      ev.target.value = '';
                    }} />
                  </label>
                  <button onClick={() => startEdit(o)} className="text-[10px] px-2 py-1 rounded hover:bg-[#F7F5F2]" style={{ color: '#2A7B88' }}>Edit</button>
                  <select value={o.status} onChange={e => updateStatus(o.id, e.target.value)}
                    className="text-[11px] px-2 py-1 rounded-full font-medium border-0" style={{ backgroundColor: (statusColors[o.status] || '#6B7280') + '15', color: statusColors[o.status] || '#6B7280' }}>
                    {(data.statuses || []).map(s => <option key={s}>{s}</option>)}
                  </select>
                  <button onClick={() => del(o.id)} className="text-[10px] px-2 py-1 rounded hover:bg-red-50" style={{ color: '#D4726A' }}>Del</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FI({ l, v, o, t = 'text' }) {
  return (
    <div>
      <label className="text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>{l}</label>
      <input type={t} value={v || ''} onChange={e => o(e.target.value)} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
    </div>
  );
}


// ============================================================
// SECTION 15 — CROCKERY & LINEN
// ============================================================
function CrockeryLinenSection({ encodedName }) {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ category: 'Crockery', uom: 'pcs' });
  const [editId, setEditId] = useState(null);
  const [catFilter, setCatFilter] = useState('');
  const importRef = useRef(null);

  const fetchData = () => api.get(`/outlet-intel/${encodedName}/crockery-linen`).then(r => setData(r.data)).catch(() => {});
  useEffect(() => { fetchData(); }, [encodedName]);

  const save = async () => {
    if (!form.item_name) { alert('Item name required'); return; }
    if (editId) await api.put(`/outlet-intel/${encodedName}/crockery-linen/${editId}`, form);
    else await api.post(`/outlet-intel/${encodedName}/crockery-linen`, form);
    setShowForm(false); setForm({ category: 'Crockery', uom: 'pcs' }); setEditId(null); fetchData();
  };
  const startEdit = (it) => { setEditId(it.id); setForm({...it}); setShowForm(true); };
  const del = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    await api.delete(`/outlet-intel/${encodedName}/crockery-linen/${id}`); fetchData();
  };
  const handleImport = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const r = await api.post(`/outlet-intel/${encodedName}/crockery-linen/import-csv`, fd); alert(`Imported ${r.data.imported} items`); fetchData(); } catch {}
    ev.target.value = '';
  };

  if (!data) return <p className="py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>Loading...</p>;
  const items = catFilter ? data.items.filter(i => i.category === catFilter) : data.items;
  const categories = ['Crockery', 'Cutlery', 'Glassware', 'Linen', 'Other'];
  const catColors = { Crockery: '#D4726A', Cutlery: '#6B7280', Glassware: '#2A7B88', Linen: '#D97706', Other: '#9CA3AF' };

  return (
    <div className="space-y-3" data-testid="section-crockery">
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Items</p><p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{data.summary.total_items}</p></div>
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Par Level</p><p className="text-lg font-bold" style={{ color: '#2A7B88' }}>{data.summary.total_par}</p></div>
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>In Use</p><p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{data.summary.total_in_use}</p></div>
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Damaged</p><p className="text-lg font-bold" style={{ color: '#DC2626' }}>{data.summary.total_damaged}</p></div>
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Breakage ₹</p><p className="text-lg font-bold" style={{ color: '#D97706' }}>{fC(data.summary.breakage_cost)}</p></div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Inventory</p>
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="px-2 py-1 text-[11px] rounded-md border" style={{ borderColor: '#E5E1DC' }}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <a href={`${API_URL}/api/outlet-intel/${encodedName}/crockery-linen/csv-template?token=${localStorage.getItem('access_token')}`} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="crockery-template-btn"><Download size={11} /> Template</a>
          <a href={`${API_URL}/api/outlet-intel/${encodedName}/crockery-linen/export-csv?token=${localStorage.getItem('access_token')}`} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="crockery-export-btn"><Download size={11} /> Export</a>
          <button onClick={() => importRef.current?.click()} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="crockery-import-btn"><Upload size={11} /> Import</button>
          <input ref={importRef} type="file" accept=".csv" className="hidden" onChange={handleImport} />
          <button onClick={() => { setShowForm(true); setEditId(null); setForm({ category: 'Crockery', uom: 'pcs' }); }} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="add-crockery-btn"><Plus size={12} /> Add</button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E5E1DC' }}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2"><FI l="Item Name" v={form.item_name} o={v => setForm(f => ({...f, item_name: v}))} /></div>
            <div>
              <label className="text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>Category</label>
              <select value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
                {categories.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <FI l="UOM" v={form.uom} o={v => setForm(f => ({...f, uom: v}))} />
            <FI l="Par Level" v={form.par_level} o={v => setForm(f => ({...f, par_level: v}))} t="number" />
            <FI l="In Use" v={form.in_use} o={v => setForm(f => ({...f, in_use: v}))} t="number" />
            <FI l="Damaged" v={form.damaged} o={v => setForm(f => ({...f, damaged: v}))} t="number" />
            <FI l="On Order" v={form.on_order} o={v => setForm(f => ({...f, on_order: v}))} t="number" />
            <FI l="Replacement Cost ₹" v={form.replacement_cost} o={v => setForm(f => ({...f, replacement_cost: v}))} t="number" />
            <FI l="Last Audit Date" v={form.last_audit_date} o={v => setForm(f => ({...f, last_audit_date: v}))} t="date" />
            <FI l="Next Audit Date" v={form.next_audit_date} o={v => setForm(f => ({...f, next_audit_date: v}))} t="date" />
            <div className="sm:col-span-3"><FI l="Notes" v={form.notes} o={v => setForm(f => ({...f, notes: v}))} /></div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={save} className="px-4 py-2 text-xs font-medium text-white rounded-md" style={{ backgroundColor: '#2A7B88' }}>{editId ? 'Update' : 'Create'}</button>
            <button onClick={() => { setShowForm(false); setEditId(null); }} className="px-4 py-2 text-xs rounded-md border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-xl border" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-sm" style={{ color: '#9CA3AF' }}>No items yet. Start your inventory by adding the first one.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-x-auto" style={{ borderColor: '#E5E1DC' }}>
          <table className="w-full text-xs">
            <thead className="border-b" style={{ backgroundColor: '#F7F5F2', borderColor: '#E5E1DC' }}>
              <tr><th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Item</th><th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Cat</th><th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Par</th><th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>In Use</th><th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Dmg</th><th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Order</th><th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Last Audit</th><th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}></th></tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id} className="border-b last:border-0" style={{ borderColor: '#E5E1DC' }}>
                  <td className="px-3 py-2" style={{ color: '#1B2A4A' }}>{it.item_name} <span className="text-[10px]" style={{ color: '#9CA3AF' }}>({it.uom})</span></td>
                  <td className="px-3 py-2"><span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: (catColors[it.category] || '#9CA3AF') + '20', color: catColors[it.category] || '#9CA3AF' }}>{it.category}</span></td>
                  <td className="px-3 py-2 text-right" style={{ color: '#1B2A4A' }}>{it.par_level}</td>
                  <td className="px-3 py-2 text-right" style={{ color: '#1B2A4A' }}>{it.in_use}</td>
                  <td className="px-3 py-2 text-right" style={{ color: it.damaged > 0 ? '#DC2626' : '#9CA3AF' }}>{it.damaged}</td>
                  <td className="px-3 py-2 text-right" style={{ color: '#1B2A4A' }}>{it.on_order}</td>
                  <td className="px-3 py-2 text-right" style={{ color: '#9CA3AF' }}>{it.last_audit_date || '—'}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => startEdit(it)} className="text-[10px] px-1.5 py-0.5 rounded hover:bg-[#F7F5F2]" style={{ color: '#2A7B88' }}>Edit</button>
                    <button onClick={() => del(it.id)} className="text-[10px] px-1.5 py-0.5 rounded hover:bg-red-50 ml-1" style={{ color: '#D4726A' }}>Del</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// SECTION 16 — ACCOMMODATION INVENTORY
// ============================================================
function AccommodationSection({ encodedName }) {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ room_type: 'Standard', status: 'Vacant Ready', max_occupancy: 2 });
  const [editId, setEditId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [checklistRoom, setChecklistRoom] = useState(null);
  const importRef = useRef(null);

  const fetchData = () => api.get(`/outlet-intel/${encodedName}/accommodation-inventory`).then(r => setData(r.data)).catch(() => {});
  useEffect(() => { fetchData(); }, [encodedName]);

  const save = async () => {
    if (!form.room_number) { alert('Room number required'); return; }
    if (editId) await api.put(`/outlet-intel/${encodedName}/accommodation-inventory/${editId}`, form);
    else await api.post(`/outlet-intel/${encodedName}/accommodation-inventory`, form);
    setShowForm(false); setForm({ room_type: 'Standard', status: 'Vacant Ready', max_occupancy: 2 }); setEditId(null); fetchData();
  };
  const startEdit = (r) => { setEditId(r.id); setForm({...r}); setShowForm(true); };
  const del = async (id) => {
    if (!window.confirm('Delete this room?')) return;
    await api.delete(`/outlet-intel/${encodedName}/accommodation-inventory/${id}`); fetchData();
  };
  const handleImport = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const r = await api.post(`/outlet-intel/${encodedName}/accommodation-inventory/import-csv`, fd); alert(`Imported ${r.data.imported} rooms`); fetchData(); } catch {}
    ev.target.value = '';
  };

  if (!data) return <p className="py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>Loading...</p>;
  const rooms = statusFilter ? data.rooms.filter(r => r.status === statusFilter) : data.rooms;
  const statuses = ['Occupied', 'Vacant Ready', 'Vacant Dirty', 'Out of Order'];
  const roomTypes = ['Standard', 'Deluxe', 'Suite', 'Villa'];
  const statusColors = { Occupied: '#DC2626', 'Vacant Ready': '#2D8B5E', 'Vacant Dirty': '#D97706', 'Out of Order': '#6B7280' };

  return (
    <div className="space-y-3" data-testid="section-accommodation">
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Total Rooms</p><p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{data.summary.total}</p></div>
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Occupancy</p><p className="text-lg font-bold" style={{ color: '#2A7B88' }}>{data.summary.occupancy_pct}%</p></div>
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Occupied</p><p className="text-lg font-bold" style={{ color: '#DC2626' }}>{data.summary.occupied}</p></div>
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Ready</p><p className="text-lg font-bold" style={{ color: '#2D8B5E' }}>{data.summary.vacant_ready}</p></div>
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>OOO</p><p className="text-lg font-bold" style={{ color: '#6B7280' }}>{data.summary.out_of_order}</p></div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Rooms</p>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-2 py-1 text-[11px] rounded-md border" style={{ borderColor: '#E5E1DC' }}>
            <option value="">All Status</option>
            {statuses.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <a href={`${API_URL}/api/outlet-intel/${encodedName}/accommodation-inventory/csv-template?token=${localStorage.getItem('access_token')}`} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="accom-template-btn"><Download size={11} /> Template</a>
          <a href={`${API_URL}/api/outlet-intel/${encodedName}/accommodation-inventory/export-csv?token=${localStorage.getItem('access_token')}`} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="accom-export-btn"><Download size={11} /> Export</a>
          <button onClick={() => importRef.current?.click()} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="accom-import-btn"><Upload size={11} /> Import</button>
          <input ref={importRef} type="file" accept=".csv" className="hidden" onChange={handleImport} />
          <button onClick={() => { setShowForm(true); setEditId(null); setForm({ room_type: 'Standard', status: 'Vacant Ready', max_occupancy: 2 }); }} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="add-room-btn"><Plus size={12} /> Add Room</button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E5E1DC' }}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <FI l="Room Number" v={form.room_number} o={v => setForm(f => ({...f, room_number: v}))} />
            <div>
              <label className="text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>Type</label>
              <select value={form.room_type} onChange={e => setForm(f => ({...f, room_type: e.target.value}))} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
                {roomTypes.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>Status</label>
              <select value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
                {statuses.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <FI l="Floor" v={form.floor} o={v => setForm(f => ({...f, floor: v}))} />
            <FI l="Max Occupancy" v={form.max_occupancy} o={v => setForm(f => ({...f, max_occupancy: v}))} t="number" />
            <FI l="Bed Configuration" v={form.bed_configuration} o={v => setForm(f => ({...f, bed_configuration: v}))} />
            <FI l="View" v={form.view} o={v => setForm(f => ({...f, view: v}))} />
            <FI l="Base Rate ₹" v={form.base_rate} o={v => setForm(f => ({...f, base_rate: v}))} t="number" />
            <FI l="Last Cleaned" v={form.last_cleaned} o={v => setForm(f => ({...f, last_cleaned: v}))} t="date" />
            <div className="sm:col-span-3"><FI l="Notes" v={form.notes} o={v => setForm(f => ({...f, notes: v}))} /></div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={save} className="px-4 py-2 text-xs font-medium text-white rounded-md" style={{ backgroundColor: '#2A7B88' }}>{editId ? 'Update' : 'Create'}</button>
            <button onClick={() => { setShowForm(false); setEditId(null); }} className="px-4 py-2 text-xs rounded-md border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          </div>
        </div>
      )}

      {rooms.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-xl border" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-sm" style={{ color: '#9CA3AF' }}>No rooms defined yet. Add your first room to start tracking.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {rooms.map(r => (
            <div key={r.id} className="bg-white rounded-lg border p-3" style={{ borderColor: '#E5E1DC' }}>
              <div className="flex items-center justify-between">
                <p className="text-base font-bold" style={{ color: '#1B2A4A' }}>{r.room_number}</p>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: (statusColors[r.status] || '#9CA3AF') + '20', color: statusColors[r.status] || '#9CA3AF' }}>{r.status}</span>
              </div>
              <p className="text-[11px] mt-1" style={{ color: '#6B7280' }}>{r.room_type} · {r.bed_configuration || 'Bed —'}</p>
              {r.view && <p className="text-[10px]" style={{ color: '#9CA3AF' }}>View: {r.view}</p>}
              <p className="text-[10px]" style={{ color: '#9CA3AF' }}>Max {r.max_occupancy} guests</p>
              {r.base_rate > 0 && <p className="text-xs font-semibold mt-1" style={{ color: '#2A7B88' }}>{fC(r.base_rate)}/night</p>}
              <div className="flex gap-1 mt-2 pt-2 border-t" style={{ borderColor: '#F3F4F6' }}>
                <button onClick={() => setChecklistRoom(r)} className="text-[10px] px-1.5 py-0.5 rounded hover:bg-[#F7F5F2]" style={{ color: '#185FA5' }} data-testid={`room-checklist-${r.id}`}>Checklist</button>
                <button onClick={() => startEdit(r)} className="text-[10px] px-1.5 py-0.5 rounded hover:bg-[#F7F5F2]" style={{ color: '#2A7B88' }}>Edit</button>
                <button onClick={() => del(r.id)} className="text-[10px] px-1.5 py-0.5 rounded hover:bg-red-50 ml-auto" style={{ color: '#D4726A' }}>Del</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {checklistRoom && <RoomChecklistModal encodedName={encodedName} room={checklistRoom} onClose={() => { setChecklistRoom(null); fetchData(); }} />}
    </div>
  );
}

// ============================================================
// SECTION 17 — SOPs
// ============================================================
function SopsSection({ encodedName }) {
  const [data, setData] = useState(null);
  const [training, setTraining] = useState(null);
  const [view, setView] = useState('list');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ category: 'Operations', status: 'Draft', version: '1.0', required_for_roles: [], required_for_departments: [] });
  const [editId, setEditId] = useState(null);
  const [catFilter, setCatFilter] = useState('');
  const [assignFor, setAssignFor] = useState(null);
  const importRef = useRef(null);

  const fetchData = () => api.get(`/outlet-intel/${encodedName}/sops`).then(r => setData(r.data)).catch(() => {});
  const fetchTraining = () => api.get(`/outlet-intel/training/outlet-dashboard/${encodedName}`).then(r => setTraining(r.data)).catch(() => {});
  useEffect(() => { fetchData(); fetchTraining(); }, [encodedName]);

  const save = async () => {
    if (!form.title) { alert('Title required'); return; }
    if (editId) await api.put(`/outlet-intel/${encodedName}/sops/${editId}`, form);
    else await api.post(`/outlet-intel/${encodedName}/sops`, form);
    setShowForm(false); setForm({ category: 'Operations', status: 'Draft', version: '1.0', required_for_roles: [], required_for_departments: [] }); setEditId(null); fetchData(); fetchTraining();
  };
  const startEdit = (s) => { setEditId(s.id); setForm({...s}); setShowForm(true); };
  const del = async (id) => {
    if (!window.confirm('Delete this SOP?')) return;
    await api.delete(`/outlet-intel/${encodedName}/sops/${id}`); fetchData();
  };
  const handleImport = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const r = await api.post(`/outlet-intel/${encodedName}/sops/import-csv`, fd); alert(`Imported ${r.data.imported} SOPs`); fetchData(); } catch {}
    ev.target.value = '';
  };
  const uploadDoc = async (sopId, ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file); fd.append('folder', 'sops');
    // Auto-detect media type from file
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    let mediaType = 'document';
    if (['mp4','mov','avi','webm','mkv','m4v'].includes(ext)) mediaType = 'video';
    else if (['mp3','m4a','wav','aac','ogg','flac','aiff'].includes(ext)) mediaType = 'audio';
    else if (['png','jpg','jpeg','webp','gif','heic'].includes(ext)) mediaType = 'image';
    try {
      const r = await api.post('/cloudinary/upload', fd);
      // Always attach to the media[] array so multiple files per SOP are supported
      await api.post(`/outlet-intel/${encodedName}/sops/${sopId}/media`, {
        url: r.data.url, name: file.name, type: mediaType, size: file.size,
      });
      // Keep legacy document_url pointing at the first uploaded document for backward compat
      if (mediaType === 'document') {
        await api.put(`/outlet-intel/${encodedName}/sops/${sopId}`, { document_url: r.data.url });
      }
      fetchData();
    } catch {}
    ev.target.value = '';
  };

  if (!data) return <p className="py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>Loading...</p>;
  const sops = catFilter ? data.sops.filter(s => s.category === catFilter) : data.sops;
  const categories = ['Operations', 'HR', 'Safety', 'Kitchen', 'Front Office', 'F&B', 'Housekeeping'];
  const statuses = ['Draft', 'Active', 'Retired'];
  const statusColors = { Draft: '#D97706', Active: '#2D8B5E', Retired: '#9CA3AF' };

  return (
    <div className="space-y-3" data-testid="section-sops">
      <div className="flex gap-1 border rounded-md overflow-hidden w-fit" style={{ borderColor: '#E5E1DC' }}>
        <button onClick={() => setView('list')} className="px-3 py-1 text-xs" style={{ backgroundColor: view === 'list' ? '#1B2A4A' : '#fff', color: view === 'list' ? '#fff' : '#6B7280' }} data-testid="sop-view-list">SOP List</button>
        <button onClick={() => setView('training')} className="px-3 py-1 text-xs" style={{ backgroundColor: view === 'training' ? '#1B2A4A' : '#fff', color: view === 'training' ? '#fff' : '#6B7280' }} data-testid="sop-view-training">Training Dashboard</button>
      </div>

      {view === 'training' && training ? (
        <TrainingDashboard data={training} />
      ) : (
      <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Total SOPs</p><p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{data.summary.total}</p></div>
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Active</p><p className="text-lg font-bold" style={{ color: '#2D8B5E' }}>{data.summary.active}</p></div>
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Draft</p><p className="text-lg font-bold" style={{ color: '#D97706' }}>{data.summary.draft}</p></div>
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Due Review</p><p className="text-lg font-bold" style={{ color: '#DC2626' }}>{data.summary.due_review}</p></div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Procedures</p>
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="px-2 py-1 text-[11px] rounded-md border" style={{ borderColor: '#E5E1DC' }}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <a href={`${API_URL}/api/outlet-intel/${encodedName}/sops/csv-template?token=${localStorage.getItem('access_token')}`} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="sops-template-btn"><Download size={11} /> Template</a>
          <a href={`${API_URL}/api/outlet-intel/${encodedName}/sops/export-csv?token=${localStorage.getItem('access_token')}`} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="sops-export-btn"><Download size={11} /> Export</a>
          <button onClick={() => importRef.current?.click()} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="sops-import-btn"><Upload size={11} /> Import</button>
          <input ref={importRef} type="file" accept=".csv" className="hidden" onChange={handleImport} />
          <button onClick={() => { setShowForm(true); setEditId(null); setForm({ category: 'Operations', status: 'Draft', version: '1.0' }); }} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="add-sop-btn"><Plus size={12} /> Add SOP</button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E5E1DC' }}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2"><FI l="Title" v={form.title} o={v => setForm(f => ({...f, title: v}))} /></div>
            <FI l="Version" v={form.version} o={v => setForm(f => ({...f, version: v}))} />
            <div>
              <label className="text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>Category</label>
              <select value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
                {categories.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>Status</label>
              <select value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
                {statuses.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <FI l="Owner" v={form.owner} o={v => setForm(f => ({...f, owner: v}))} />
            <FI l="Effective Date" v={form.effective_date} o={v => setForm(f => ({...f, effective_date: v}))} t="date" />
            <FI l="Last Review Date" v={form.last_review_date} o={v => setForm(f => ({...f, last_review_date: v}))} t="date" />
            <FI l="Next Review Date" v={form.next_review_date} o={v => setForm(f => ({...f, next_review_date: v}))} t="date" />
            <FI l="Reviewed By" v={form.reviewed_by} o={v => setForm(f => ({...f, reviewed_by: v}))} />
            <div className="sm:col-span-3">
              <label className="text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>Required for Roles (comma-separated)</label>
              <input type="text" value={(form.required_for_roles || []).join(', ')} onChange={e => setForm(f => ({...f, required_for_roles: e.target.value.split(',').map(s => s.trim()).filter(Boolean)}))}
                placeholder="e.g. Receptionist, Duty Manager"
                className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            </div>
            <div className="sm:col-span-3">
              <label className="text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>Required for Departments (comma-separated)</label>
              <input type="text" value={(form.required_for_departments || []).join(', ')} onChange={e => setForm(f => ({...f, required_for_departments: e.target.value.split(',').map(s => s.trim()).filter(Boolean)}))}
                placeholder="e.g. Front Office, Kitchen"
                className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            </div>
            <div className="sm:col-span-3">
              <label className="text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>Content / Summary</label>
              <textarea value={form.content || ''} onChange={e => setForm(f => ({...f, content: e.target.value}))} rows={4} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={save} className="px-4 py-2 text-xs font-medium text-white rounded-md" style={{ backgroundColor: '#2A7B88' }}>{editId ? 'Update' : 'Create'}</button>
            <button onClick={() => { setShowForm(false); setEditId(null); }} className="px-4 py-2 text-xs rounded-md border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          </div>
        </div>
      )}

      {sops.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-xl border" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-sm" style={{ color: '#9CA3AF' }}>No SOPs documented yet. Add the first one to build your operations manual.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sops.map(s => (
            <div key={s.id} className="bg-white rounded-xl border p-3" style={{ borderColor: '#E5E1DC' }}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>{s.title}</p>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: (statusColors[s.status] || '#9CA3AF') + '20', color: statusColors[s.status] || '#9CA3AF' }}>{s.status}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#F7F5F2', color: '#6B7280' }}>{s.category}</span>
                    <span className="text-[10px]" style={{ color: '#9CA3AF' }}>v{s.version}</span>
                  </div>
                  <p className="text-[11px] mt-0.5" style={{ color: '#6B7280' }}>
                    {s.owner && `Owner: ${s.owner}`}
                    {s.next_review_date && ` · Next Review: ${s.next_review_date}`}
                    {s.reviewed_by && ` · Reviewed by: ${s.reviewed_by}`}
                  </p>
                  {s.content && <p className="text-[10px] mt-1 line-clamp-2" style={{ color: '#9CA3AF' }}>{s.content}</p>}
                  {Array.isArray(s.media) && s.media.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`sop-media-${s.id}`}>
                      {s.media.map((m) => {
                        const icon = m.type === 'video' ? '▶' : m.type === 'audio' ? '♪' : m.type === 'image' ? '🖼' : '📄';
                        const bg = m.type === 'video' ? '#FEF2F2' : m.type === 'audio' ? '#ECFDF5' : m.type === 'image' ? '#FEF3C7' : '#EFF6FF';
                        const fg = m.type === 'video' ? '#D4726A' : m.type === 'audio' ? '#2D8B5E' : m.type === 'image' ? '#BA7517' : '#185FA5';
                        return (
                          <a key={m.id} href={m.url} target="_blank" rel="noreferrer" title={m.name}
                            className="text-[10px] px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 hover:underline"
                            style={{ backgroundColor: bg, color: fg }}>
                            <span>{icon}</span><span className="max-w-[120px] truncate">{m.name}</span>
                          </a>
                        );
                      })}
                    </div>
                  )}
                  {/* Translation Listen pills — QA any translated narration in-place */}
                  <SopListenPills sopId={s.id} />
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {s.document_url && <a href={s.document_url} target="_blank" rel="noreferrer" className="text-[10px] px-2 py-1 rounded bg-blue-50 text-blue-700">Doc</a>}
                  <label className="text-[10px] px-2 py-1 rounded hover:bg-[#F7F5F2] cursor-pointer" style={{ color: '#2A7B88' }} title="Upload document, video, audio, or image">
                    Upload
                    <input type="file" className="hidden" accept=".pdf,.doc,.docx,.mp4,.mov,.m4v,.webm,.mp3,.m4a,.wav,.aac,image/*" onChange={(ev) => uploadDoc(s.id, ev)} />
                  </label>
                  <button onClick={() => setAssignFor(s)} className="text-[10px] px-2 py-1 rounded hover:bg-[#F7F5F2]" style={{ color: '#185FA5' }} data-testid={`assign-sop-${s.id}`}>Assign</button>
                  <button onClick={() => startEdit(s)} className="text-[10px] px-2 py-1 rounded hover:bg-[#F7F5F2]" style={{ color: '#2A7B88' }}>Edit</button>
                  <button onClick={() => del(s.id)} className="text-[10px] px-2 py-1 rounded hover:bg-red-50" style={{ color: '#D4726A' }}>Del</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </>
      )}

      {assignFor && <AssignSopModal sop={assignFor} encodedName={encodedName} onClose={() => setAssignFor(null)} onDone={() => { setAssignFor(null); fetchTraining(); }} />}
    </div>
  );
}

function TrainingDashboard({ data }) {
  const s = data.summary;
  const [nudging, setNudging] = useState(false);
  const [nudgeResult, setNudgeResult] = useState(null);

  const nudgeUnacknowledged = async (dryRun = false) => {
    setNudging(true); setNudgeResult(null);
    try {
      const r = await api.post(`/outlet-intel/training/nudge-unacknowledged?dry_run=${dryRun}`);
      setNudgeResult(r.data);
    } catch (err) {
      setNudgeResult({ error: err.response?.data?.detail || 'Nudge failed' });
    } finally {
      setNudging(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="training-dashboard">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Active SOPs</p><p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{s.total_sops}</p></div>
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Assignments</p><p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{s.total_assignments}</p></div>
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Acknowledged</p><p className="text-lg font-bold" style={{ color: '#2D8B5E' }}>{s.acknowledged}</p></div>
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Completion</p><p className="text-lg font-bold" style={{ color: s.overall_completion_pct >= 80 ? '#2D8B5E' : s.overall_completion_pct >= 50 ? '#D97706' : '#DC2626' }}>{s.overall_completion_pct}%</p></div>
      </div>

      {/* WhatsApp Nudge Bar */}
      <div className="rounded-lg border p-2.5 flex items-center justify-between gap-2 flex-wrap" style={{ borderColor: '#E5E1DC', backgroundColor: '#FFFBEB' }}>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold" style={{ color: '#BA7517' }}>WhatsApp Nudge · Pending SOPs</p>
          <p className="text-[10px]" style={{ color: '#92400E' }}>Send a friendly reminder via Gupshup to every team member with unacknowledged SOPs across all outlets.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => nudgeUnacknowledged(true)} disabled={nudging} className="px-2.5 py-1.5 rounded-md text-[11px] font-medium border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="nudge-dry-run-btn">Preview (Dry Run)</button>
          <button onClick={() => nudgeUnacknowledged(false)} disabled={nudging} className="px-3 py-1.5 rounded-md text-[11px] font-semibold text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="nudge-send-btn">{nudging ? 'Sending…' : 'Send Nudges'}</button>
        </div>
      </div>

      {nudgeResult && (
        <div className="rounded-md p-2.5 text-[11px]" style={{ backgroundColor: nudgeResult.error ? '#FEF2F2' : '#ECFDF5', color: nudgeResult.error ? '#7F1D1D' : '#065F46' }} data-testid="nudge-result">
          {nudgeResult.error ? (
            <p>Error: {nudgeResult.error}</p>
          ) : (
            <>
              <p className="font-semibold">{nudgeResult.dry_run ? 'Preview' : 'Nudge complete'}: {nudgeResult.total_people_with_pending} people pending · {nudgeResult.sent} sent · {nudgeResult.skipped_no_phone_or_gupshup} skipped</p>
              {nudgeResult.preview?.length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {nudgeResult.preview.slice(0, 5).map((p, i) => (
                    <li key={i}>• {p.name} — {p.pending} pending ({p.mobile || 'no mobile'})</li>
                  ))}
                  {nudgeResult.preview.length > 5 && <li>… +{nudgeResult.preview.length - 5} more</li>}
                </ul>
              )}
            </>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="bg-white rounded-xl border p-3" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>By SOP</p>
          {data.sops.length === 0 ? <p className="text-xs" style={{ color: '#9CA3AF' }}>No active SOPs.</p> : (
            <div className="space-y-1.5">
              {data.sops.map(r => (
                <div key={r.sop_id} className="flex items-center gap-2 text-xs py-1">
                  <span className="flex-1 truncate" style={{ color: '#1B2A4A' }}>{r.title}</span>
                  <div className="w-28 h-1.5 rounded-full" style={{ backgroundColor: '#F3F4F6' }}>
                    <div className="h-full rounded-full" style={{ width: `${r.completion_pct}%`, backgroundColor: r.completion_pct >= 80 ? '#2D8B5E' : r.completion_pct >= 50 ? '#D97706' : '#DC2626' }} />
                  </div>
                  <span className="w-10 text-right text-[10px]" style={{ color: '#6B7280' }}>{r.acknowledged}/{r.total_assigned}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border p-3" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>By Team Member (worst first)</p>
          {data.team.length === 0 ? <p className="text-xs" style={{ color: '#9CA3AF' }}>No team members assigned to SOPs yet.</p> : (
            <div className="space-y-1.5">
              {data.team.slice(0, 10).map(t => (
                <div key={t.person_id} className="flex items-center gap-2 text-xs py-1">
                  <span className="flex-1 truncate" style={{ color: '#1B2A4A' }}>{t.name} <span className="text-[10px]" style={{ color: '#9CA3AF' }}>({t.role})</span></span>
                  <div className="w-20 h-1.5 rounded-full" style={{ backgroundColor: '#F3F4F6' }}>
                    <div className="h-full rounded-full" style={{ width: `${t.completion_pct}%`, backgroundColor: t.completion_pct >= 80 ? '#2D8B5E' : t.completion_pct >= 50 ? '#D97706' : '#DC2626' }} />
                  </div>
                  <span className="w-10 text-right text-[10px]" style={{ color: '#6B7280' }}>{t.completed}/{t.assigned}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AssignSopModal({ sop, encodedName, onClose, onDone }) {
  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/people').then(r => setPeople(r.data || [])).catch(() => {});
  }, []);

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const submit = async () => {
    if (selected.length === 0) { alert('Select at least one person'); return; }
    await api.post(`/outlet-intel/${encodedName}/sops/${sop.id}/assign`, { person_ids: selected });
    onDone();
  };

  const filtered = (people || []).filter(p => !search || (p.name || '').toLowerCase().includes(search.toLowerCase()) || (p.outlet || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-2" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white rounded-lg p-4 w-full max-w-lg max-h-[90vh] flex flex-col" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Assign: {sop.title}</p>
          <button onClick={onClose} className="text-xs px-2 py-1 rounded hover:bg-gray-50">×</button>
        </div>
        <input type="search" placeholder="Search people..." value={search} onChange={e => setSearch(e.target.value)} className="w-full px-3 py-2 rounded border text-sm mb-2" style={{ borderColor: '#E5E1DC' }} />
        <div className="flex-1 overflow-y-auto space-y-1 min-h-0">
          {filtered.map(p => (
            <label key={p.id} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggle(p.id)} />
              <span className="text-xs flex-1" style={{ color: '#1B2A4A' }}>{p.name}</span>
              <span className="text-[10px]" style={{ color: '#9CA3AF' }}>{p.role} · {p.outlet || '—'}</span>
            </label>
          ))}
        </div>
        <div className="flex gap-2 mt-3 pt-3 border-t" style={{ borderColor: '#F3F4F6' }}>
          <p className="text-[11px]" style={{ color: '#6B7280' }}>{selected.length} selected</p>
          <button onClick={submit} className="ml-auto px-4 py-2 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="submit-assign-btn">Assign</button>
          <button onClick={onClose} className="px-4 py-2 rounded-md text-xs border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// SECTION 20 — COMMON AREAS
// ============================================================
function CommonAreasSection({ encodedName }) {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: 'Lobby', condition: 'Good', status: 'Active', cleaning_frequency: 'Daily' });
  const [editId, setEditId] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const importRef = useRef(null);

  const fetchData = () => api.get(`/outlet-intel/${encodedName}/common-areas`).then(r => setData(r.data)).catch(() => {});
  useEffect(() => { fetchData(); }, [encodedName]);

  const save = async () => {
    if (!form.name) { alert('Name required'); return; }
    if (editId) await api.put(`/outlet-intel/${encodedName}/common-areas/${editId}`, form);
    else await api.post(`/outlet-intel/${encodedName}/common-areas`, form);
    setShowForm(false); setForm({ type: 'Lobby', condition: 'Good', status: 'Active', cleaning_frequency: 'Daily' }); setEditId(null); fetchData();
  };
  const startEdit = (a) => { setEditId(a.id); setForm({...a}); setShowForm(true); };
  const del = async (id) => {
    if (!window.confirm('Delete this area?')) return;
    await api.delete(`/outlet-intel/${encodedName}/common-areas/${id}`); fetchData();
  };
  const handleImport = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const r = await api.post(`/outlet-intel/${encodedName}/common-areas/import-csv`, fd); alert(`Imported ${r.data.imported} areas`); fetchData(); } catch {}
    ev.target.value = '';
  };
  const uploadPhoto = async (areaId, ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file); fd.append('folder', 'common-areas');
    try {
      const r = await api.post('/cloudinary/upload', fd);
      await api.put(`/outlet-intel/${encodedName}/common-areas/${areaId}`, { photo_url: r.data.url });
      fetchData();
    } catch {}
    ev.target.value = '';
  };

  if (!data) return <p className="py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>Loading...</p>;
  const areas = typeFilter ? data.areas.filter(a => a.type === typeFilter) : data.areas;
  const types = ['Lobby', 'Pool', 'Gym', 'Garden', 'Parking', 'Banquet', 'Restroom', 'Other'];
  const conditions = ['Excellent', 'Good', 'Fair', 'Poor'];
  const statuses = ['Active', 'Closed', 'Under Renovation'];
  const frequencies = ['Hourly', 'Daily', 'Twice Daily', 'Weekly', 'Monthly'];
  const condColors = { Excellent: '#2D8B5E', Good: '#2A7B88', Fair: '#D97706', Poor: '#DC2626' };

  return (
    <div className="space-y-3" data-testid="section-common">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Areas</p><p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{data.summary.total}</p></div>
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Active</p><p className="text-lg font-bold" style={{ color: '#2D8B5E' }}>{data.summary.active}</p></div>
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Poor Condition</p><p className="text-lg font-bold" style={{ color: '#DC2626' }}>{data.summary.poor_condition}</p></div>
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}><p className="text-[10px] uppercase" style={{ color: '#9CA3AF' }}>Due Inspection</p><p className="text-lg font-bold" style={{ color: '#D97706' }}>{data.summary.due_inspection}</p></div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Shared Spaces</p>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="px-2 py-1 text-[11px] rounded-md border" style={{ borderColor: '#E5E1DC' }}>
            <option value="">All Types</option>
            {types.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <a href={`${API_URL}/api/outlet-intel/${encodedName}/common-areas/csv-template?token=${localStorage.getItem('access_token')}`} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="common-template-btn"><Download size={11} /> Template</a>
          <a href={`${API_URL}/api/outlet-intel/${encodedName}/common-areas/export-csv?token=${localStorage.getItem('access_token')}`} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="common-export-btn"><Download size={11} /> Export</a>
          <button onClick={() => importRef.current?.click()} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="common-import-btn"><Upload size={11} /> Import</button>
          <input ref={importRef} type="file" accept=".csv" className="hidden" onChange={handleImport} />
          <button onClick={() => { setShowForm(true); setEditId(null); setForm({ type: 'Lobby', condition: 'Good', status: 'Active', cleaning_frequency: 'Daily' }); }} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="add-common-btn"><Plus size={12} /> Add Area</button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E5E1DC' }}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2"><FI l="Area Name" v={form.name} o={v => setForm(f => ({...f, name: v}))} /></div>
            <div>
              <label className="text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>Type</label>
              <select value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value}))} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
                {types.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <FI l="Area (sq ft)" v={form.area_sqft} o={v => setForm(f => ({...f, area_sqft: v}))} t="number" />
            <FI l="Capacity" v={form.capacity} o={v => setForm(f => ({...f, capacity: v}))} t="number" />
            <div>
              <label className="text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>Condition</label>
              <select value={form.condition} onChange={e => setForm(f => ({...f, condition: e.target.value}))} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
                {conditions.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>Status</label>
              <select value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
                {statuses.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <FI l="Owner" v={form.owner} o={v => setForm(f => ({...f, owner: v}))} />
            <div>
              <label className="text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>Cleaning Freq.</label>
              <select value={form.cleaning_frequency} onChange={e => setForm(f => ({...f, cleaning_frequency: e.target.value}))} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
                {frequencies.map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
            <FI l="Last Cleaned" v={form.last_cleaned} o={v => setForm(f => ({...f, last_cleaned: v}))} t="date" />
            <FI l="Last Inspection" v={form.last_inspection_date} o={v => setForm(f => ({...f, last_inspection_date: v}))} t="date" />
            <FI l="Next Inspection" v={form.next_inspection_date} o={v => setForm(f => ({...f, next_inspection_date: v}))} t="date" />
            <FI l="Monthly Cost ₹ (Rent + Utilities + Maint)" v={form.monthly_cost} o={v => setForm(f => ({...f, monthly_cost: v}))} t="number" />
            <div className="sm:col-span-3"><FI l="Notes" v={form.notes} o={v => setForm(f => ({...f, notes: v}))} /></div>
            <div className="sm:col-span-3">
              <p className="text-[10px] font-semibold uppercase mb-1" style={{ color: '#6B7280' }}>Cost Allocation (must sum to 100%)</p>
              <div className="space-y-2">
                {(form.cost_allocation || []).map((a, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input type="text" placeholder="Outlet (or HQ)" value={a.outlet || ''} onChange={e => {
                      const newAllocs = [...(form.cost_allocation || [])];
                      newAllocs[i] = {...newAllocs[i], outlet: e.target.value};
                      setForm(f => ({...f, cost_allocation: newAllocs}));
                    }} className="flex-1 px-2 py-1 text-xs rounded border" style={{ borderColor: '#E5E1DC' }} />
                    <input type="number" placeholder="%" value={a.pct || ''} onChange={e => {
                      const newAllocs = [...(form.cost_allocation || [])];
                      newAllocs[i] = {...newAllocs[i], pct: parseFloat(e.target.value) || 0};
                      setForm(f => ({...f, cost_allocation: newAllocs}));
                    }} className="w-16 px-2 py-1 text-xs rounded border text-right" style={{ borderColor: '#E5E1DC' }} />
                    <button onClick={() => {
                      const newAllocs = (form.cost_allocation || []).filter((_, j) => j !== i);
                      setForm(f => ({...f, cost_allocation: newAllocs}));
                    }} className="text-[10px] px-1.5 py-0.5 rounded hover:bg-red-50" style={{ color: '#D4726A' }}>×</button>
                  </div>
                ))}
                <button onClick={() => setForm(f => ({...f, cost_allocation: [...(f.cost_allocation || []), {outlet: '', pct: 0}]}))}
                  className="text-[11px] px-2 py-1 rounded hover:bg-gray-50 flex items-center gap-1" style={{ color: '#2A7B88' }}><Plus size={11}/> Add allocation</button>
                {(form.cost_allocation || []).length > 0 && (
                  <p className="text-[10px]" style={{ color: (form.cost_allocation || []).reduce((s, a) => s + (parseFloat(a.pct) || 0), 0) === 100 ? '#2D8B5E' : '#D97706' }}>
                    Total: {(form.cost_allocation || []).reduce((s, a) => s + (parseFloat(a.pct) || 0), 0)}%
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={save} className="px-4 py-2 text-xs font-medium text-white rounded-md" style={{ backgroundColor: '#2A7B88' }}>{editId ? 'Update' : 'Create'}</button>
            <button onClick={() => { setShowForm(false); setEditId(null); }} className="px-4 py-2 text-xs rounded-md border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          </div>
        </div>
      )}

      {areas.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-xl border" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-sm" style={{ color: '#9CA3AF' }}>No common areas defined yet. Add your first one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {areas.map(a => (
            <div key={a.id} className="bg-white rounded-xl border p-3" style={{ borderColor: '#E5E1DC' }}>
              {a.photo_url && <img src={a.photo_url} alt={a.name} className="w-full h-24 object-cover rounded-md mb-2" />}
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>{a.name}</p>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: (condColors[a.condition] || '#9CA3AF') + '20', color: condColors[a.condition] || '#9CA3AF' }}>{a.condition}</span>
              </div>
              <p className="text-[11px] mt-1" style={{ color: '#6B7280' }}>{a.type}{a.area_sqft > 0 && ` · ${a.area_sqft} sq ft`}{a.capacity > 0 && ` · Cap ${a.capacity}`}</p>
              {a.owner && <p className="text-[10px]" style={{ color: '#9CA3AF' }}>Owner: {a.owner}</p>}
              <p className="text-[10px]" style={{ color: '#9CA3AF' }}>Clean: {a.cleaning_frequency}{a.last_cleaned && ` · last ${a.last_cleaned}`}</p>
              {a.next_inspection_date && <p className="text-[10px]" style={{ color: '#D97706' }}>Next inspection: {a.next_inspection_date}</p>}
              {a.monthly_cost > 0 && (
                <div className="mt-1 pt-1 border-t" style={{ borderColor: '#F3F4F6' }}>
                  <p className="text-[10px] font-medium" style={{ color: '#6B7280' }}>Monthly Cost: {fC(a.monthly_cost)}</p>
                  {(a.cost_allocation || []).length > 0 && (
                    <p className="text-[10px]" style={{ color: '#9CA3AF' }}>
                      {(a.cost_allocation || []).map(ca => `${ca.outlet} ${ca.pct}%`).join(' · ')}
                    </p>
                  )}
                </div>
              )}
              <div className="flex gap-1 mt-2 pt-2 border-t" style={{ borderColor: '#F3F4F6' }}>
                <label className="text-[10px] px-1.5 py-0.5 rounded hover:bg-[#F7F5F2] cursor-pointer" style={{ color: '#2A7B88' }}>
                  Photo
                  <input type="file" accept="image/*" className="hidden" onChange={(ev) => uploadPhoto(a.id, ev)} />
                </label>
                <button onClick={() => startEdit(a)} className="text-[10px] px-1.5 py-0.5 rounded hover:bg-[#F7F5F2]" style={{ color: '#2A7B88' }}>Edit</button>
                <button onClick={() => del(a.id)} className="text-[10px] px-1.5 py-0.5 rounded hover:bg-red-50 ml-auto" style={{ color: '#D4726A' }}>Del</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ============================================================
// SECTION 5B — REVIEWS & REPUTATION
// ============================================================
function ReviewsSection({ encodedName, outletName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ platform: 'Google', review_date: new Date().toISOString().slice(0, 10), rating: 5, reviewer_name: '', review_text: '', response_status: 'Pending', response_text: '', response_date: '' });
  const [filterPlatform, setFilterPlatform] = useState('');
  const fileInputRef = useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    setLoading(true);
    try {
      const q = filterPlatform ? `?platform=${encodeURIComponent(filterPlatform)}` : '';
      const { data } = await api.get(`/outlet-intel/${encodedName}/reviews${q}`);
      setData(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [encodedName, filterPlatform]);

  const save = async () => {
    try {
      if (editing) await api.put(`/outlet-intel/${encodedName}/reviews/${editing}`, form);
      else await api.post(`/outlet-intel/${encodedName}/reviews`, form);
      setShowForm(false); setEditing(null);
      setForm({ platform: 'Google', review_date: new Date().toISOString().slice(0, 10), rating: 5, reviewer_name: '', review_text: '', response_status: 'Pending', response_text: '', response_date: '' });
      await load();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.detail || e.message)); }
  };
  const startEdit = (r) => { setEditing(r.id); setForm({ platform: r.platform, review_date: r.review_date, rating: r.rating, reviewer_name: r.reviewer_name || '', review_text: r.review_text || '', response_status: r.response_status || 'Pending', response_text: r.response_text || '', response_date: r.response_date || '' }); setShowForm(true); };
  const del = async (id) => { if (!window.confirm('Delete this review?')) return; try { await api.delete(`/outlet-intel/${encodedName}/reviews/${id}`); await load(); } catch (e) { alert('Delete failed'); } };
  const exportCSV = async () => {
    const token = localStorage.getItem('access_token');
    try { const r = await fetch(`${BACKEND}/api/outlet-intel/${encodedName}/reviews/export-csv`, { headers: { Authorization: `Bearer ${token}` } }); const blob = await r.blob(); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${outletName.replace(/\s+/g, '_')}_reviews.csv`; a.click(); } catch { alert('Export failed'); }
  };
  const downloadTemplate = async () => {
    const token = localStorage.getItem('access_token');
    try { const r = await fetch(`${BACKEND}/api/outlet-intel/${encodedName}/reviews/csv-template`, { headers: { Authorization: `Bearer ${token}` } }); const blob = await r.blob(); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${outletName.replace(/\s+/g, '_')}_reviews_template.csv`; a.click(); } catch { alert('Template failed'); }
  };
  const importCSV = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const { data } = await api.post(`/outlet-intel/${encodedName}/reviews/import-csv`, fd); alert(`Imported ${data.created} reviews${data.errors?.length ? ` (${data.errors.length} errors)` : ''}`); await load(); }
    catch (err) { alert('Import failed: ' + (err.response?.data?.detail || err.message)); }
    finally { e.target.value = ''; }
  };

  if (loading) return <p className="py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>Loading reviews...</p>;
  if (!data) return null;

  const s = data.summary;
  const changeColor = s.rating_change > 0 ? '#2D8B5E' : s.rating_change < 0 ? '#DC2626' : '#9CA3AF';
  const platformColor = { Google: '#4285F4', Zomato: '#CB202D', Swiggy: '#FC8019', TripAdvisor: '#00AF87', Direct: '#185FA5', Other: '#6B7280' };

  return (
    <div className="space-y-4" data-testid="section-reviews-content">
      {/* Summary header */}
      <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#6B7280' }}>Last 30 days</p>
            <div className="flex items-baseline gap-2">
              <p className="text-3xl font-bold" style={{ color: s.avg_rating_30d >= 4.5 ? '#2D8B5E' : s.avg_rating_30d >= 4 ? '#D97706' : '#DC2626' }}>{s.avg_rating_30d || '—'}</p>
              <p className="text-sm" style={{ color: '#9CA3AF' }}>★ avg · {s.reviews_30d} reviews</p>
            </div>
            {s.avg_rating_prev30 > 0 && (
              <p className="text-[11px] mt-1" style={{ color: changeColor }}>{s.rating_change > 0 ? '+' : ''}{s.rating_change} vs prev 30d ({s.avg_rating_prev30}★)</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <p className="text-[10px] uppercase" style={{ color: '#6B7280' }}>Negative Pending</p>
              <p className="text-2xl font-bold" style={{ color: s.negative_pending_count > 0 ? '#DC2626' : '#9CA3AF' }}>{s.negative_pending_count}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase" style={{ color: '#6B7280' }}>All-time</p>
              <p className="text-2xl font-bold" style={{ color: '#1B2A4A' }}>{s.total_reviews}</p>
            </div>
          </div>
        </div>

        {/* Per-platform summary */}
        {data.platform_summary.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-3 pt-3 border-t" style={{ borderColor: '#F3F4F6' }}>
            {data.platform_summary.map(p => (
              <button key={p.platform} onClick={() => setFilterPlatform(filterPlatform === p.platform ? '' : p.platform)}
                className="text-left p-2 rounded-md border transition-colors"
                style={{ borderColor: filterPlatform === p.platform ? platformColor[p.platform] || '#1B2A4A' : '#E5E1DC', backgroundColor: filterPlatform === p.platform ? (platformColor[p.platform] || '#1B2A4A') + '10' : '#fff' }}
                data-testid={`platform-chip-${p.platform}`}>
                <p className="text-[10px] uppercase font-semibold" style={{ color: platformColor[p.platform] || '#6B7280' }}>{p.platform}</p>
                <p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{p.avg_rating}★ <span className="text-[10px] font-normal" style={{ color: '#9CA3AF' }}>({p.count})</span></p>
                {p.count_30d > 0 && <p className="text-[9px]" style={{ color: '#9CA3AF' }}>30d: {p.avg_rating_30d}★ · {p.count_30d}</p>}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 30-day trend chart */}
      {data.trend_30d.length > 0 && (
        <div className="bg-white rounded-xl border p-3" style={{ borderColor: '#E5E1DC' }} data-testid="reviews-trend-chart">
          <p className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>30-Day Rating Trend</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={data.trend_30d}>
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#9CA3AF' }} tickFormatter={(d) => d ? d.slice(5) : ''} />
              <YAxis domain={[0, 5]} tick={{ fontSize: 9, fill: '#9CA3AF' }} />
              <Tooltip />
              <Bar dataKey="avg_rating" fill="#185FA5" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Negative alerts */}
      {data.negative_pending.length > 0 && (
        <div className="bg-[#FEF2F2] rounded-xl border p-3" style={{ borderColor: '#FCA5A5' }} data-testid="reviews-negative-alerts">
          <p className="text-xs font-semibold mb-2" style={{ color: '#991B1B' }}>Negative Reviews Awaiting Response ({data.negative_pending.length})</p>
          <div className="space-y-1.5">
            {data.negative_pending.map(r => (
              <div key={r.id} className="bg-white rounded-md p-2 text-xs flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate" style={{ color: '#1B2A4A' }}><span style={{ color: platformColor[r.platform] || '#6B7280' }}>{r.platform}</span> · {r.rating}★ · {r.reviewer_name || 'Anonymous'} <span className="text-[10px]" style={{ color: '#9CA3AF' }}>· {r.review_date}</span></p>
                  {r.review_text && <p className="text-[11px] truncate" style={{ color: '#6B7280' }}>{r.review_text}</p>}
                </div>
                <button onClick={() => startEdit(data.reviews.find(x => x.id === r.id))} className="text-[10px] px-2 py-0.5 rounded text-white flex-shrink-0" style={{ backgroundColor: '#DC2626' }}>Respond</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>All Reviews ({data.total})</p>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="add-review-btn"><Plus size={11} /> Add</button>
          <button onClick={downloadTemplate} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="reviews-template-btn"><Download size={11} /> Template</button>
          <button onClick={exportCSV} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="reviews-export-btn"><Download size={11} /> Export</button>
          <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="reviews-import-btn"><Upload size={11} /> Import</button>
          <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={importCSV} />
        </div>
      </div>

      {/* Add/Edit form */}
      {showForm && (
        <div className="bg-white rounded-xl border p-4 space-y-2" style={{ borderColor: '#2A7B88', borderWidth: 2 }} data-testid="review-form">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <label className="text-xs" style={{ color: '#6B7280' }}>Platform
              <select value={form.platform} onChange={e => setForm(f => ({ ...f, platform: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }}>
                {(data.platforms || ['Google', 'Zomato', 'Swiggy', 'TripAdvisor', 'Direct', 'Other']).map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Date
              <input type="date" value={form.review_date} onChange={e => setForm(f => ({ ...f, review_date: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Rating (1-5)
              <input type="number" min="1" max="5" step="0.5" value={form.rating} onChange={e => setForm(f => ({ ...f, rating: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Reviewer
              <input value={form.reviewer_name} onChange={e => setForm(f => ({ ...f, reviewer_name: e.target.value }))} placeholder="Name or Anonymous" className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
          </div>
          <label className="text-xs block" style={{ color: '#6B7280' }}>Review text
            <textarea rows={2} value={form.review_text} onChange={e => setForm(f => ({ ...f, review_text: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <label className="text-xs" style={{ color: '#6B7280' }}>Response status
              <select value={form.response_status} onChange={e => setForm(f => ({ ...f, response_status: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }}>
                <option value="Pending">Pending</option>
                <option value="Responded">Responded</option>
                <option value="Escalated">Escalated</option>
              </select>
            </label>
            <label className="text-xs sm:col-span-2" style={{ color: '#6B7280' }}>Response text
              <input value={form.response_text} onChange={e => setForm(f => ({ ...f, response_text: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-xs px-3 py-1.5 rounded border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
            <button onClick={save} className="text-xs px-3 py-1.5 rounded text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="review-save-btn">{editing ? 'Update' : 'Save'}</button>
          </div>
        </div>
      )}

      {/* Review list */}
      {data.reviews.length === 0 ? (
        <p className="py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>No reviews recorded yet.</p>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
          <div className="divide-y" style={{ borderColor: '#F3F4F6' }}>
            {data.reviews.map(r => (
              <div key={r.id} className="p-3 hover:bg-[#FAFAF8]">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded" style={{ color: platformColor[r.platform] || '#6B7280', backgroundColor: (platformColor[r.platform] || '#6B7280') + '15' }}>{r.platform}</span>
                      <span className="text-sm font-bold" style={{ color: r.rating >= 4 ? '#2D8B5E' : r.rating >= 3 ? '#D97706' : '#DC2626' }}>{r.rating}★</span>
                      <span className="text-[11px]" style={{ color: '#6B7280' }}>{r.reviewer_name || 'Anonymous'}</span>
                      <span className="text-[10px]" style={{ color: '#9CA3AF' }}>{r.review_date}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: r.response_status === 'Responded' ? '#ECFDF5' : r.response_status === 'Escalated' ? '#FEF2F2' : '#FEF3C7', color: r.response_status === 'Responded' ? '#065F46' : r.response_status === 'Escalated' ? '#991B1B' : '#92400E' }}>{r.response_status}</span>
                    </div>
                    {r.review_text && <p className="text-[12px] mt-1" style={{ color: '#374151' }}>{r.review_text}</p>}
                    {r.response_text && <p className="text-[11px] mt-1 pl-3 border-l-2" style={{ color: '#6B7280', borderColor: '#2A7B88' }}><strong>Response:</strong> {r.response_text}{r.response_date ? <span className="text-[10px]" style={{ color: '#9CA3AF' }}> · {r.response_date}</span> : null}</p>}
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => startEdit(r)} className="text-[10px] px-2 py-0.5 rounded hover:bg-[#F7F5F2]" style={{ color: '#2A7B88' }}>Edit</button>
                    <button onClick={() => del(r.id)} className="text-[10px] px-2 py-0.5 rounded hover:bg-red-50" style={{ color: '#D4726A' }}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// COST-CENTRE ALLOCATION EDITOR
// ============================================================
function AllocationEditor({ encodedName, costCentre, canEdit, divisionId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState([]);
  const [outletOptions, setOutletOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRows((costCentre.allocation || []).map(a => ({ outlet: a.outlet, pct: a.pct })));
  }, [costCentre]);

  useEffect(() => {
    if (!divisionId) return;
    api.get(`/outlets?division_id=${divisionId}`)
      .then(r => setOutletOptions((r.data || []).filter(o => !o.is_cost_centre && o.outlet_type !== 'cost_centre' && !(o.name || '').toLowerCase().includes('common area') && !(o.name || '').toLowerCase().startsWith('hq'))))
      .catch(() => setOutletOptions([]));
  }, [divisionId]);

  const sum = rows.reduce((a, r) => a + (parseFloat(r.pct) || 0), 0);

  const save = async () => {
    if (Math.abs(sum - 100) > 0.5 && rows.length > 0) { alert(`Allocation must sum to 100% (currently ${sum}%)`); return; }
    setSaving(true);
    try {
      await api.put(`/outlet-intel/${encodedName}/cost-allocation`, { allocation: rows });
      setEditing(false);
      if (onSaved) await onSaved();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  return (
    <div className="border-t pt-3" style={{ borderColor: '#F3F4F6' }} data-testid="cc-allocation-editor">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#6B7280' }}>Cost Allocation — where does this land?</p>
        {canEdit && !editing && (
          <button onClick={() => setEditing(true)} className="text-[10px] px-2 py-0.5 rounded hover:bg-[#F7F5F2]" style={{ color: '#2A7B88' }} data-testid="cc-alloc-edit-btn">Edit allocation</button>
        )}
      </div>

      {!editing ? (
        costCentre.downstream_impact && costCentre.downstream_impact.length > 0 ? (
          <div className="space-y-1">
            {costCentre.downstream_impact.map((d, i) => (
              <div key={i} className="flex items-center justify-between text-xs bg-[#F7F5F2] rounded px-2 py-1.5" data-testid={`cc-alloc-${i}`}>
                <span style={{ color: '#1B2A4A' }}>{d.target}</span>
                <span className="flex items-center gap-2">
                  <span className="font-medium" style={{ color: '#D97706' }}>{d.pct}%</span>
                  <span style={{ color: '#6B7280' }}>≈ {fC(d.monthly_rupees)}/mo</span>
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs" style={{ color: '#9CA3AF' }}>No allocation set — costs flow into division HQ overhead.</p>
        )
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <select value={r.outlet || ''} onChange={e => { const nr = [...rows]; nr[i] = { ...nr[i], outlet: e.target.value }; setRows(nr); }}
                className="flex-1 text-xs px-2 py-1 rounded border" style={{ borderColor: '#E5E1DC' }} data-testid={`cc-alloc-outlet-${i}`}>
                <option value="">Select outlet…</option>
                {outletOptions.map(o => <option key={o.id || o.name} value={o.name}>{o.name}</option>)}
              </select>
              <input type="number" min="0" max="100" step="0.1" value={r.pct}
                onChange={e => { const nr = [...rows]; nr[i] = { ...nr[i], pct: parseFloat(e.target.value) || 0 }; setRows(nr); }}
                className="w-20 text-xs px-2 py-1 rounded border text-right" style={{ borderColor: '#E5E1DC' }} data-testid={`cc-alloc-pct-${i}`} />
              <span className="text-xs" style={{ color: '#6B7280' }}>%</span>
              <button onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-[10px] text-[#D4726A] px-1.5 py-1 rounded hover:bg-red-50">Remove</button>
            </div>
          ))}
          <button onClick={() => setRows([...rows, { outlet: '', pct: 0 }])} className="text-[11px] flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-50" style={{ color: '#2A7B88' }} data-testid="cc-alloc-add-row"><Plus size={11} /> Add target</button>
          <div className="flex items-center justify-between pt-1 border-t" style={{ borderColor: '#F3F4F6' }}>
            <p className="text-[11px]" style={{ color: Math.abs(sum - 100) > 0.5 ? '#DC2626' : '#2D8B5E' }}>Total: {sum.toFixed(1)}% {Math.abs(sum - 100) > 0.5 ? `(must be 100%)` : '✓'}</p>
            <div className="flex items-center gap-1.5">
              <button onClick={() => { setEditing(false); setRows((costCentre.allocation || []).map(a => ({ outlet: a.outlet, pct: a.pct }))); }} className="text-[11px] px-2 py-1 rounded border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>Cancel</button>
              <button onClick={save} disabled={saving} className="text-[11px] px-3 py-1 rounded text-white" style={{ backgroundColor: '#1B2A4A', opacity: saving ? 0.6 : 1 }} data-testid="cc-alloc-save-btn">{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================
// SECTION 18 — PREVENTIVE MAINTENANCE SCHEDULER
// ============================================================
function PMSchedulesSection({ encodedName, outletName, userRole }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', asset_name: '', location_detail: '', category: 'General Maintenance', priority: 'Normal', frequency: 'Monthly', start_date: new Date().toISOString().slice(0, 10), next_due: '', assigned_to: '', estimated_cost: 0, active: true });
  const fileInputRef = useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const canEdit = ['ceo', 'leader', 'sub_leader'].includes(userRole);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/outlet-intel/${encodedName}/pm-schedules`); setData(data); }
    catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [encodedName]);

  const save = async () => {
    if (!form.title) { alert('Title required'); return; }
    const payload = { ...form, next_due: form.next_due || form.start_date };
    try {
      if (editing) await api.put(`/outlet-intel/${encodedName}/pm-schedules/${editing}`, payload);
      else await api.post(`/outlet-intel/${encodedName}/pm-schedules`, payload);
      setShowForm(false); setEditing(null);
      setForm({ title: '', description: '', asset_name: '', location_detail: '', category: 'General Maintenance', priority: 'Normal', frequency: 'Monthly', start_date: new Date().toISOString().slice(0, 10), next_due: '', assigned_to: '', estimated_cost: 0, active: true });
      await load();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.detail || e.message)); }
  };
  const startEdit = (s) => { setEditing(s.id); setForm({ ...s }); setShowForm(true); };
  const del = async (id) => { if (!window.confirm('Delete this PM schedule?')) return; try { await api.delete(`/outlet-intel/${encodedName}/pm-schedules/${id}`); await load(); } catch (e) { alert('Delete failed'); } };
  const generateOne = async (id) => { try { const { data } = await api.post(`/outlet-intel/${encodedName}/pm-schedules/${id}/generate-wo`); alert(`Work order created. Next due: ${data.next_due}`); await load(); } catch (e) { alert('Generate failed'); } };
  const runAllDue = async () => {
    if (!window.confirm('Generate work orders for all overdue PM schedules?')) return;
    try { const { data } = await api.post(`/outlet-intel/${encodedName}/pm-schedules/run-all`); alert(`${data.count} work orders generated`); await load(); }
    catch (e) { alert('Run-all failed: ' + (e.response?.data?.detail || e.message)); }
  };
  const exportCSV = async () => {
    const token = localStorage.getItem('access_token');
    try { const r = await fetch(`${BACKEND}/api/outlet-intel/${encodedName}/pm-schedules/export-csv`, { headers: { Authorization: `Bearer ${token}` } }); const blob = await r.blob(); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${outletName.replace(/\s+/g, '_')}_pm_schedules.csv`; a.click(); } catch { alert('Export failed'); }
  };
  const downloadTemplate = async () => {
    const token = localStorage.getItem('access_token');
    try { const r = await fetch(`${BACKEND}/api/outlet-intel/${encodedName}/pm-schedules/csv-template`, { headers: { Authorization: `Bearer ${token}` } }); const blob = await r.blob(); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${outletName.replace(/\s+/g, '_')}_pm_template.csv`; a.click(); } catch { alert('Template failed'); }
  };
  const importCSV = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const { data } = await api.post(`/outlet-intel/${encodedName}/pm-schedules/import-csv`, fd); alert(`Imported ${data.created} schedules${data.errors?.length ? ` (${data.errors.length} errors)` : ''}`); await load(); }
    catch (err) { alert('Import failed: ' + (err.response?.data?.detail || err.message)); }
    finally { e.target.value = ''; }
  };

  if (loading) return <p className="py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>Loading PM schedules...</p>;
  if (!data) return null;

  return (
    <div className="space-y-3" data-testid="section-pm">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-[9px] uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Total Schedules</p>
          <p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{data.total}</p>
        </div>
        <div className="bg-[#FEF2F2] rounded-lg border p-2.5" style={{ borderColor: '#FCA5A5' }}>
          <p className="text-[9px] uppercase tracking-wide font-semibold" style={{ color: '#991B1B' }}>Overdue</p>
          <p className="text-lg font-bold" style={{ color: '#DC2626' }}>{data.overdue_count}</p>
        </div>
        <div className="bg-[#FFFBEB] rounded-lg border p-2.5" style={{ borderColor: '#D9770640' }}>
          <p className="text-[9px] uppercase tracking-wide font-semibold" style={{ color: '#D97706' }}>Due in 7 days</p>
          <p className="text-lg font-bold" style={{ color: '#D97706' }}>{data.upcoming_count}</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Preventive Maintenance Schedules</p>
        <div className="flex items-center gap-1.5 flex-wrap">
          {canEdit && data.overdue_count > 0 && (
            <button onClick={runAllDue} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded text-white" style={{ backgroundColor: '#DC2626' }} data-testid="pm-run-all-btn">Generate all overdue WOs</button>
          )}
          {canEdit && <button onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="pm-add-btn"><Plus size={11} /> Add Schedule</button>}
          <button onClick={downloadTemplate} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="pm-template-btn"><Download size={11} /> Template</button>
          <button onClick={exportCSV} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="pm-export-btn"><Download size={11} /> Export</button>
          {canEdit && <>
            <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="pm-import-btn"><Upload size={11} /> Import</button>
            <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={importCSV} />
          </>}
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl border p-4 space-y-2" style={{ borderColor: '#2A7B88', borderWidth: 2 }} data-testid="pm-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="text-xs" style={{ color: '#6B7280' }}>Title*
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Asset / Equipment
              <input value={form.asset_name} onChange={e => setForm(f => ({ ...f, asset_name: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Location
              <input value={form.location_detail} onChange={e => setForm(f => ({ ...f, location_detail: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Category
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }}>
                {(data.categories || ['General Maintenance']).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Priority
              <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }}>
                <option>Emergency</option><option>Urgent</option><option>Normal</option><option>Planned</option>
              </select>
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Frequency
              <select value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }}>
                {(data.frequencies || ['Monthly']).map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Start Date
              <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Next Due
              <input type="date" value={form.next_due || form.start_date} onChange={e => setForm(f => ({ ...f, next_due: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Assigned To
              <input value={form.assigned_to} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Est. Cost ₹
              <input type="number" value={form.estimated_cost} onChange={e => setForm(f => ({ ...f, estimated_cost: parseFloat(e.target.value) || 0 }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
            <label className="text-xs sm:col-span-2 flex items-end gap-2 pb-1.5">
              <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} />
              <span style={{ color: '#6B7280' }}>Active (WO will auto-generate when due)</span>
            </label>
          </div>
          <label className="text-xs block" style={{ color: '#6B7280' }}>Description / checklist
            <textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-xs px-3 py-1.5 rounded border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
            <button onClick={save} className="text-xs px-3 py-1.5 rounded text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="pm-save-btn">{editing ? 'Update' : 'Save'}</button>
          </div>
        </div>
      )}

      {/* Schedule list */}
      {data.schedules.length === 0 ? (
        <p className="py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>No PM schedules yet. Add recurring maintenance tasks like "HVAC service every 90 days".</p>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
          <div className="divide-y" style={{ borderColor: '#F3F4F6' }}>
            {data.schedules.map(s => (
              <div key={s.id} className="p-3 hover:bg-[#FAFAF8]" style={{ borderLeft: s.is_overdue ? '3px solid #DC2626' : s.is_upcoming ? '3px solid #D97706' : 'none', paddingLeft: (s.is_overdue || s.is_upcoming) ? 10 : 12 }}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>{s.title}</p>
                      <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}>{s.frequency}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: s.priority === 'Emergency' ? '#FEF2F2' : s.priority === 'Urgent' ? '#FFFBEB' : '#F3F4F6', color: s.priority === 'Emergency' ? '#991B1B' : s.priority === 'Urgent' ? '#92400E' : '#6B7280' }}>{s.priority}</span>
                      {!s.active && <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: '#F3F4F6', color: '#9CA3AF' }}>Paused</span>}
                      {s.is_overdue && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: '#DC2626', color: '#fff' }}>OVERDUE</span>}
                      {s.is_upcoming && !s.is_overdue && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: '#D97706', color: '#fff' }}>Due soon</span>}
                    </div>
                    <p className="text-[11px] mt-0.5" style={{ color: '#6B7280' }}>
                      {s.asset_name && <span>{s.asset_name} · </span>}
                      {s.location_detail && <span>{s.location_detail} · </span>}
                      <span>Next: <strong style={{ color: s.is_overdue ? '#DC2626' : '#1B2A4A' }}>{s.next_due || '—'}</strong></span>
                      {s.assigned_to && <span> · {s.assigned_to}</span>}
                      {s.estimated_cost > 0 && <span> · Est. {fC(s.estimated_cost)}</span>}
                    </p>
                    {s.description && <p className="text-[11px] mt-0.5" style={{ color: '#374151' }}>{s.description}</p>}
                  </div>
                  <div className="flex flex-col gap-1">
                    {canEdit && <button onClick={() => generateOne(s.id)} className="text-[10px] px-2 py-0.5 rounded text-white" style={{ backgroundColor: '#2A7B88' }} data-testid={`pm-gen-${s.id}`}>Generate WO</button>}
                    {canEdit && <button onClick={() => startEdit(s)} className="text-[10px] px-2 py-0.5 rounded hover:bg-[#F7F5F2]" style={{ color: '#2A7B88' }}>Edit</button>}
                    {canEdit && <button onClick={() => del(s.id)} className="text-[10px] px-2 py-0.5 rounded hover:bg-red-50" style={{ color: '#D4726A' }}>Delete</button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================
// SECTION 9 — OPERATIONS & INCIDENTS
// ============================================================
function OperationsSection({ encodedName, outletName, userRole }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ops_type: 'Equipment Breakdown', event_date: new Date().toISOString().slice(0, 10), event_time: '', title: '', description: '', severity: 'Low', status: 'Open', reported_by: '', affected_party: '', asset_name: '', location_detail: '', grn_compliant: false, grn_vendor: '', grn_invoice_no: '', grn_amount: 0, grn_po_matched: false, resolution: '', cost_incurred: 0 });
  const fileInputRef = useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const canEdit = ['ceo', 'leader', 'sub_leader', 'sourcer'].includes(userRole);

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filterType) q.append('type', filterType);
      if (filterStatus) q.append('status', filterStatus);
      const { data } = await api.get(`/outlet-intel/${encodedName}/operations${q.toString() ? '?' + q.toString() : ''}`);
      setData(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [encodedName, filterType, filterStatus]);

  const save = async () => {
    if (!form.title) { alert('Title required'); return; }
    try {
      if (editing) await api.put(`/outlet-intel/${encodedName}/operations/${editing}`, form);
      else await api.post(`/outlet-intel/${encodedName}/operations`, form);
      setShowForm(false); setEditing(null);
      setForm({ ops_type: 'Equipment Breakdown', event_date: new Date().toISOString().slice(0, 10), event_time: '', title: '', description: '', severity: 'Low', status: 'Open', reported_by: '', affected_party: '', asset_name: '', location_detail: '', grn_compliant: false, grn_vendor: '', grn_invoice_no: '', grn_amount: 0, grn_po_matched: false, resolution: '', cost_incurred: 0 });
      await load();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.detail || e.message)); }
  };
  const startEdit = (it) => { setEditing(it.id); setForm({ ...form, ...it }); setShowForm(true); };
  const del = async (id) => { if (!window.confirm('Delete this entry?')) return; try { await api.delete(`/outlet-intel/${encodedName}/operations/${id}`); await load(); } catch (e) { alert('Delete failed'); } };
  const exportCSV = async () => {
    const token = localStorage.getItem('access_token');
    try { const r = await fetch(`${BACKEND}/api/outlet-intel/${encodedName}/operations/export-csv`, { headers: { Authorization: `Bearer ${token}` } }); const blob = await r.blob(); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${outletName.replace(/\s+/g, '_')}_operations.csv`; a.click(); } catch { alert('Export failed'); }
  };
  const downloadTemplate = async () => {
    const token = localStorage.getItem('access_token');
    try { const r = await fetch(`${BACKEND}/api/outlet-intel/${encodedName}/operations/csv-template`, { headers: { Authorization: `Bearer ${token}` } }); const blob = await r.blob(); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${outletName.replace(/\s+/g, '_')}_operations_template.csv`; a.click(); } catch { alert('Template failed'); }
  };
  const importCSV = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const { data } = await api.post(`/outlet-intel/${encodedName}/operations/import-csv`, fd); alert(`Imported ${data.created}${data.errors?.length ? ` (${data.errors.length} errors)` : ''}`); await load(); }
    catch (err) { alert('Import failed: ' + (err.response?.data?.detail || err.message)); }
    finally { e.target.value = ''; }
  };

  if (loading) return <p className="py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>Loading...</p>;
  if (!data) return null;

  const s = data.summary;
  const typeColor = { GRN: '#185FA5', 'Equipment Breakdown': '#DC2626', 'Guest Incident': '#D97706', 'Staff Incident': '#7C3AED', Safety: '#2D8B5E', Other: '#6B7280' };
  const sevColor = { Low: '#6B7280', Medium: '#D97706', High: '#DC2626', Critical: '#991B1B' };

  return (
    <div className="space-y-3" data-testid="section-operations">
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-[9px] uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Last 30 days</p>
          <p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{s.total}</p>
        </div>
        <div className="bg-[#FEF2F2] rounded-lg border p-2.5" style={{ borderColor: '#FCA5A5' }}>
          <p className="text-[9px] uppercase tracking-wide font-semibold" style={{ color: '#991B1B' }}>Open</p>
          <p className="text-lg font-bold" style={{ color: '#DC2626' }}>{s.open}</p>
          {s.critical_open > 0 && <p className="text-[9px]" style={{ color: '#991B1B' }}>{s.critical_open} critical</p>}
        </div>
        <div className="bg-[#EFF6FF] rounded-lg border p-2.5" style={{ borderColor: '#185FA540' }}>
          <p className="text-[9px] uppercase tracking-wide font-semibold" style={{ color: '#185FA5' }}>GRN Compliance</p>
          <p className="text-lg font-bold" style={{ color: '#185FA5' }}>{s.grn_compliance_pct !== null ? `${s.grn_compliance_pct}%` : '—'}</p>
          {s.grn_total > 0 && <p className="text-[9px]" style={{ color: '#6B7280' }}>{s.grn_compliant}/{s.grn_total} compliant</p>}
        </div>
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-[9px] uppercase tracking-wide" style={{ color: '#9CA3AF' }}>By Type (30d)</p>
          <div className="flex flex-wrap gap-0.5 mt-0.5">
            {Object.entries(s.by_type || {}).map(([t, c]) => (
              <span key={t} className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: (typeColor[t] || '#6B7280') + '20', color: typeColor[t] || '#6B7280' }}>{t}: {c}</span>
            ))}
            {Object.keys(s.by_type || {}).length === 0 && <span className="text-[10px]" style={{ color: '#9CA3AF' }}>—</span>}
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <select value={filterType} onChange={e => setFilterType(e.target.value)} className="px-2 py-1 text-[11px] rounded border" style={{ borderColor: '#E5E1DC' }} data-testid="ops-filter-type">
            <option value="">All types</option>
            {(data.types || []).map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-2 py-1 text-[11px] rounded border" style={{ borderColor: '#E5E1DC' }} data-testid="ops-filter-status">
            <option value="">All statuses</option>
            {(data.statuses || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {canEdit && <button onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="ops-add-btn"><Plus size={11} /> Log Entry</button>}
          <button onClick={downloadTemplate} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="ops-template-btn"><Download size={11} /> Template</button>
          <button onClick={exportCSV} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="ops-export-btn"><Download size={11} /> Export</button>
          {canEdit && <>
            <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="ops-import-btn"><Upload size={11} /> Import</button>
            <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={importCSV} />
          </>}
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl border p-4 space-y-2" style={{ borderColor: '#2A7B88', borderWidth: 2 }} data-testid="ops-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="text-xs" style={{ color: '#6B7280' }}>Type
              <select value={form.ops_type} onChange={e => setForm(f => ({ ...f, ops_type: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }}>
                {(data.types || []).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Date
              <input type="date" value={form.event_date} onChange={e => setForm(f => ({ ...f, event_date: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Time
              <input type="time" value={form.event_time} onChange={e => setForm(f => ({ ...f, event_time: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
            <label className="text-xs sm:col-span-2" style={{ color: '#6B7280' }}>Title*
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Severity
              <select value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }}>
                {(data.severities || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Location
              <input value={form.location_detail} onChange={e => setForm(f => ({ ...f, location_detail: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Asset (if any)
              <input value={form.asset_name} onChange={e => setForm(f => ({ ...f, asset_name: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Affected Party (guest / staff)
              <input value={form.affected_party} onChange={e => setForm(f => ({ ...f, affected_party: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
          </div>
          <label className="text-xs block" style={{ color: '#6B7280' }}>Description
            <textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
          </label>

          {/* GRN-specific fields */}
          {form.ops_type === 'GRN' && (
            <div className="bg-[#EFF6FF] rounded-md p-3 space-y-2">
              <p className="text-[10px] uppercase font-semibold" style={{ color: '#185FA5' }}>GRN Details</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <label className="text-xs" style={{ color: '#6B7280' }}>Vendor
                  <input value={form.grn_vendor} onChange={e => setForm(f => ({ ...f, grn_vendor: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
                </label>
                <label className="text-xs" style={{ color: '#6B7280' }}>Invoice No.
                  <input value={form.grn_invoice_no} onChange={e => setForm(f => ({ ...f, grn_invoice_no: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
                </label>
                <label className="text-xs" style={{ color: '#6B7280' }}>Amount ₹
                  <input type="number" value={form.grn_amount} onChange={e => setForm(f => ({ ...f, grn_amount: parseFloat(e.target.value) || 0 }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
                </label>
                <div className="flex flex-col gap-1 pt-4">
                  <label className="text-xs flex items-center gap-1.5" style={{ color: '#6B7280' }}>
                    <input type="checkbox" checked={form.grn_compliant} onChange={e => setForm(f => ({ ...f, grn_compliant: e.target.checked }))} /> Compliant
                  </label>
                  <label className="text-xs flex items-center gap-1.5" style={{ color: '#6B7280' }}>
                    <input type="checkbox" checked={form.grn_po_matched} onChange={e => setForm(f => ({ ...f, grn_po_matched: e.target.checked }))} /> PO matched
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="text-xs" style={{ color: '#6B7280' }}>Status
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }}>
                {(data.statuses || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Cost Incurred ₹
              <input type="number" value={form.cost_incurred} onChange={e => setForm(f => ({ ...f, cost_incurred: parseFloat(e.target.value) || 0 }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Resolution
              <input value={form.resolution} onChange={e => setForm(f => ({ ...f, resolution: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-xs px-3 py-1.5 rounded border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
            <button onClick={save} className="text-xs px-3 py-1.5 rounded text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="ops-save-btn">{editing ? 'Update' : 'Save'}</button>
          </div>
        </div>
      )}

      {/* List */}
      {data.operations.length === 0 ? (
        <p className="py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>No operations logged yet.</p>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
          <div className="divide-y" style={{ borderColor: '#F3F4F6' }}>
            {data.operations.map(it => (
              <div key={it.id} className="p-3 hover:bg-[#FAFAF8]">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded" style={{ color: typeColor[it.ops_type] || '#6B7280', backgroundColor: (typeColor[it.ops_type] || '#6B7280') + '15' }}>{it.ops_type}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: (sevColor[it.severity] || '#6B7280') + '20', color: sevColor[it.severity] || '#6B7280' }}>{it.severity}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: it.status === 'Open' ? '#FEF2F2' : it.status === 'In Progress' ? '#FFFBEB' : '#ECFDF5', color: it.status === 'Open' ? '#991B1B' : it.status === 'In Progress' ? '#92400E' : '#065F46' }}>{it.status}</span>
                      <span className="text-[10px]" style={{ color: '#9CA3AF' }}>{it.event_date}{it.event_time ? ` ${it.event_time}` : ''}</span>
                    </div>
                    <p className="text-sm font-medium mt-0.5" style={{ color: '#1B2A4A' }}>{it.title}</p>
                    {it.description && <p className="text-[11px]" style={{ color: '#6B7280' }}>{it.description}</p>}
                    <div className="flex flex-wrap gap-2 text-[10px] mt-1" style={{ color: '#9CA3AF' }}>
                      {it.location_detail && <span>Loc: {it.location_detail}</span>}
                      {it.asset_name && <span>Asset: {it.asset_name}</span>}
                      {it.affected_party && <span>Party: {it.affected_party}</span>}
                      {it.ops_type === 'GRN' && it.grn_vendor && <span>{it.grn_vendor} {it.grn_invoice_no ? `· ${it.grn_invoice_no}` : ''} {it.grn_amount > 0 ? `· ${fC(it.grn_amount)}` : ''}</span>}
                      {it.ops_type === 'GRN' && <span style={{ color: it.grn_compliant ? '#2D8B5E' : '#DC2626' }}>{it.grn_compliant ? '✓ Compliant' : '✗ Non-compliant'}</span>}
                      {it.cost_incurred > 0 && <span>Cost: {fC(it.cost_incurred)}</span>}
                    </div>
                    {it.resolution && <p className="text-[11px] mt-1 pl-3 border-l-2" style={{ color: '#6B7280', borderColor: '#2A7B88' }}><strong>Resolution:</strong> {it.resolution}{it.resolved_at ? ` (${it.resolved_at})` : ''}</p>}
                  </div>
                  {canEdit && (
                    <div className="flex flex-col gap-1">
                      <button onClick={() => startEdit(it)} className="text-[10px] px-2 py-0.5 rounded hover:bg-[#F7F5F2]" style={{ color: '#2A7B88' }}>Edit</button>
                      <button onClick={() => del(it.id)} className="text-[10px] px-2 py-0.5 rounded hover:bg-red-50" style={{ color: '#D4726A' }}>Delete</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================
// SECTION 16-B — ROOM-ITEM INVENTORY + GUEST-READY CHECKLIST MODAL
// ============================================================
function RoomChecklistModal({ encodedName, room, onClose }) {
  const [tab, setTab] = useState('items');
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemForm, setItemForm] = useState({ item_name: '', category: 'Linens', par_quantity: 1, current_quantity: 0, unit: 'pcs', notes: '' });
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [checklistNotes, setChecklistNotes] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [inv, chks] = await Promise.all([
        api.get(`/outlet-intel/${encodedName}/rooms/${room.id}/inventory`),
        api.get(`/outlet-intel/${encodedName}/rooms/${room.id}/checklists`),
      ]);
      setData(inv.data);
      setHistory(chks.data.checklists || []);
      const defaults = inv.data.default_checklist || [];
      setChecklist(defaults.map(text => ({ text, checked: false, note: '' })));
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [room.id]);

  const saveItem = async () => {
    if (!itemForm.item_name) { alert('Item name required'); return; }
    try {
      if (editingItem) await api.put(`/outlet-intel/${encodedName}/rooms/${room.id}/inventory/${editingItem}`, itemForm);
      else await api.post(`/outlet-intel/${encodedName}/rooms/${room.id}/inventory`, itemForm);
      setShowItemForm(false); setEditingItem(null);
      setItemForm({ item_name: '', category: 'Linens', par_quantity: 1, current_quantity: 0, unit: 'pcs', notes: '' });
      await load();
    } catch (e) { alert('Save failed'); }
  };
  const delItem = async (id) => { if (!window.confirm('Delete?')) return; try { await api.delete(`/outlet-intel/${encodedName}/rooms/${room.id}/inventory/${id}`); await load(); } catch {} };
  const submitChecklist = async () => {
    try {
      const { data: res } = await api.post(`/outlet-intel/${encodedName}/rooms/${room.id}/checklist`, { items: checklist, notes: checklistNotes });
      alert(`Checklist saved — ${res.pass_pct}% pass. ${res.guest_ready ? 'Room marked Vacant Ready.' : 'Room not yet guest-ready.'}`);
      setChecklistNotes('');
      await load();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const passed = checklist.filter(c => c.checked).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-testid="room-checklist-modal">
        <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: '#E5E1DC' }}>
          <div>
            <h3 className="text-base font-semibold" style={{ color: '#1B2A4A' }}>Room {room.room_number}</h3>
            <p className="text-[11px]" style={{ color: '#6B7280' }}>{room.room_type} · {room.status}</p>
          </div>
          <button onClick={onClose} className="text-xs px-2 py-1 rounded hover:bg-gray-100" style={{ color: '#6B7280' }}>Close</button>
        </div>
        <div className="flex border-b" style={{ borderColor: '#E5E1DC' }}>
          {['items', 'checklist', 'history'].map(t => (
            <button key={t} onClick={() => setTab(t)} className="px-4 py-2 text-xs font-medium"
              style={{ borderBottom: tab === t ? '2px solid #1B2A4A' : '2px solid transparent', color: tab === t ? '#1B2A4A' : '#6B7280' }}
              data-testid={`room-tab-${t}`}>
              {t === 'items' ? 'Inventory' : t === 'checklist' ? 'Guest-Ready Checklist' : 'History'}
            </button>
          ))}
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {loading ? <p className="text-center text-sm" style={{ color: '#9CA3AF' }}>Loading...</p> : (
            <>
              {tab === 'items' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>Items ({data?.items?.length || 0})</p>
                    <button onClick={() => { setEditingItem(null); setShowItemForm(true); }} className="text-[11px] flex items-center gap-1 px-2 py-1 rounded text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="room-add-item"><Plus size={11} />Add Item</button>
                  </div>
                  {showItemForm && (
                    <div className="bg-[#F7F5F2] rounded p-2 space-y-2">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <input placeholder="Item name" value={itemForm.item_name} onChange={e => setItemForm(f => ({ ...f, item_name: e.target.value }))} className="text-xs px-2 py-1 rounded border" style={{ borderColor: '#E5E1DC' }} />
                        <select value={itemForm.category} onChange={e => setItemForm(f => ({ ...f, category: e.target.value }))} className="text-xs px-2 py-1 rounded border" style={{ borderColor: '#E5E1DC' }}>
                          <option>Linens</option><option>Toiletries</option><option>Electronics</option><option>Furniture</option><option>Consumables</option><option>Amenities</option><option>Other</option>
                        </select>
                        <input type="number" placeholder="Par" value={itemForm.par_quantity} onChange={e => setItemForm(f => ({ ...f, par_quantity: parseInt(e.target.value) || 0 }))} className="text-xs px-2 py-1 rounded border" style={{ borderColor: '#E5E1DC' }} />
                        <input type="number" placeholder="Current" value={itemForm.current_quantity} onChange={e => setItemForm(f => ({ ...f, current_quantity: parseInt(e.target.value) || 0 }))} className="text-xs px-2 py-1 rounded border" style={{ borderColor: '#E5E1DC' }} />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button onClick={() => { setShowItemForm(false); setEditingItem(null); }} className="text-[11px] px-2 py-1 rounded border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
                        <button onClick={saveItem} className="text-[11px] px-3 py-1 rounded text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="room-item-save">{editingItem ? 'Update' : 'Save'}</button>
                      </div>
                    </div>
                  )}
                  {(data?.items || []).length === 0 ? (
                    <p className="text-center text-xs py-4" style={{ color: '#9CA3AF' }}>No items yet. Add towels, linens, toiletries, etc.</p>
                  ) : (
                    <div className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                      {(data.items || []).map(it => {
                        const short = (it.current_quantity || 0) < (it.par_quantity || 0);
                        return (
                          <div key={it.id} className="py-2 flex items-center gap-2">
                            <div className="flex-1">
                              <p className="text-sm" style={{ color: '#1B2A4A' }}>{it.item_name}</p>
                              <p className="text-[10px]" style={{ color: '#9CA3AF' }}>{it.category} · par {it.par_quantity} {it.unit}</p>
                            </div>
                            <span className="text-sm font-semibold" style={{ color: short ? '#DC2626' : '#2D8B5E' }}>{it.current_quantity}/{it.par_quantity}</span>
                            <button onClick={() => { setEditingItem(it.id); setItemForm({ item_name: it.item_name, category: it.category, par_quantity: it.par_quantity, current_quantity: it.current_quantity, unit: it.unit, notes: it.notes || '' }); setShowItemForm(true); }} className="text-[10px] px-1.5 py-0.5 rounded hover:bg-[#F7F5F2]" style={{ color: '#2A7B88' }}>Edit</button>
                            <button onClick={() => delItem(it.id)} className="text-[10px] px-1.5 py-0.5 rounded hover:bg-red-50" style={{ color: '#D4726A' }}>Del</button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {tab === 'checklist' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>Guest-Ready Checklist</p>
                    <p className="text-xs" style={{ color: passed === checklist.length ? '#2D8B5E' : '#D97706' }}>{passed}/{checklist.length} passed</p>
                  </div>
                  <div className="space-y-1 max-h-[40vh] overflow-y-auto">
                    {checklist.map((c, i) => (
                      <label key={i} className="flex items-start gap-2 p-1.5 rounded hover:bg-[#FAFAF8] cursor-pointer text-xs" data-testid={`checklist-item-${i}`}>
                        <input type="checkbox" checked={c.checked} onChange={e => { const nc = [...checklist]; nc[i] = { ...nc[i], checked: e.target.checked }; setChecklist(nc); }} className="mt-0.5" />
                        <span style={{ color: c.checked ? '#2D8B5E' : '#374151' }}>{c.text}</span>
                      </label>
                    ))}
                  </div>
                  <textarea rows={2} placeholder="Notes / issues..." value={checklistNotes} onChange={e => setChecklistNotes(e.target.value)} className="w-full text-xs px-2 py-1.5 rounded border" style={{ borderColor: '#E5E1DC' }} />
                  <div className="flex justify-end">
                    <button onClick={submitChecklist} className="text-xs px-3 py-1.5 rounded text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="checklist-submit">Submit Checklist</button>
                  </div>
                </div>
              )}
              {tab === 'history' && (
                history.length === 0 ? <p className="text-center text-xs py-4" style={{ color: '#9CA3AF' }}>No checklist history yet.</p> : (
                  <div className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                    {history.map(h => (
                      <div key={h.id} className="py-2">
                        <div className="flex items-center justify-between text-xs">
                          <span style={{ color: '#1B2A4A' }}>{h.check_date} · {h.checked_by}</span>
                          <span style={{ color: h.guest_ready ? '#2D8B5E' : '#D97706' }}>{h.pass_pct}% {h.guest_ready ? '✓ Guest-Ready' : ''}</span>
                        </div>
                        {h.notes && <p className="text-[11px]" style={{ color: '#6B7280' }}>{h.notes}</p>}
                      </div>
                    ))}
                  </div>
                )
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================================
// BAR VARIANCE (P2)
// ============================================================
function BarVarianceSection({ encodedName, outletName, userRole }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ item_name: '', category: 'Whisky', sku_code: '', bottle_size_ml: 750, opening_stock: 0, physical_count: 0, par_quantity: 0, unit_cost: 0, selling_price: 0 });
  const [countMode, setCountMode] = useState(false);
  const [countEntries, setCountEntries] = useState({});
  const fileInputRef = useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const canEdit = ['ceo', 'leader', 'sub_leader'].includes(userRole);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/outlet-intel/${encodedName}/bar-sku`); setData(data); }
    catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [encodedName]);

  const save = async () => {
    if (!form.item_name) { alert('Item name required'); return; }
    try {
      if (editing) await api.put(`/outlet-intel/${encodedName}/bar-sku/${editing}`, form);
      else await api.post(`/outlet-intel/${encodedName}/bar-sku`, form);
      setShowForm(false); setEditing(null);
      setForm({ item_name: '', category: 'Whisky', sku_code: '', bottle_size_ml: 750, opening_stock: 0, physical_count: 0, par_quantity: 0, unit_cost: 0, selling_price: 0 });
      await load();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.detail || e.message)); }
  };
  const startEdit = (s) => { setEditing(s.id); setForm({ ...s }); setShowForm(true); };
  const del = async (id) => { if (!window.confirm('Delete SKU?')) return; try { await api.delete(`/outlet-intel/${encodedName}/bar-sku/${id}`); await load(); } catch {} };
  const submitCount = async () => {
    const items = Object.entries(countEntries).filter(([, v]) => v && v.physical_count !== '' && v.physical_count !== undefined).map(([sku_id, v]) => ({ sku_id, physical_count: parseFloat(v.physical_count) || 0, purchases_since: parseFloat(v.purchases_since) || 0, sales_since: parseFloat(v.sales_since) || 0 }));
    if (items.length === 0) { alert('No count entries'); return; }
    try {
      const { data: res } = await api.post(`/outlet-intel/${encodedName}/bar-sku/count`, { items, notes: '' });
      alert(`Count saved. Total variance value: ${res.total_variance_value >= 0 ? '+' : ''}${res.total_variance_value}`);
      setCountEntries({}); setCountMode(false);
      await load();
    } catch (e) { alert('Count save failed: ' + (e.response?.data?.detail || e.message)); }
  };
  const exportCSV = async () => {
    const token = localStorage.getItem('access_token');
    try { const r = await fetch(`${BACKEND}/api/outlet-intel/${encodedName}/bar-sku/export-csv`, { headers: { Authorization: `Bearer ${token}` } }); const blob = await r.blob(); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${outletName.replace(/\s+/g, '_')}_bar.csv`; a.click(); } catch { alert('Export failed'); }
  };
  const downloadTemplate = async () => {
    const token = localStorage.getItem('access_token');
    try { const r = await fetch(`${BACKEND}/api/outlet-intel/${encodedName}/bar-sku/csv-template`, { headers: { Authorization: `Bearer ${token}` } }); const blob = await r.blob(); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${outletName.replace(/\s+/g, '_')}_bar_template.csv`; a.click(); } catch { alert('Template failed'); }
  };
  const importCSV = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const { data } = await api.post(`/outlet-intel/${encodedName}/bar-sku/import-csv`, fd); alert(`Imported ${data.created}${data.errors?.length ? ` (${data.errors.length} errors)` : ''}`); await load(); }
    catch (err) { alert('Import failed: ' + (err.response?.data?.detail || err.message)); }
    finally { e.target.value = ''; }
  };

  if (loading) return <p className="py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>Loading bar SKUs...</p>;
  if (!data) return null;

  return (
    <div className="space-y-3" data-testid="section-bar-variance">
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-[9px] uppercase tracking-wide" style={{ color: '#9CA3AF' }}>SKUs</p>
          <p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{data.total_skus}</p>
        </div>
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-[9px] uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Inventory Value</p>
          <p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{fC(data.total_inventory_value)}</p>
        </div>
        <div className={`rounded-lg border p-2.5 ${data.variance_value_30d < 0 ? 'bg-[#FEF2F2]' : 'bg-[#ECFDF5]'}`} style={{ borderColor: data.variance_value_30d < 0 ? '#FCA5A5' : '#10B98140' }}>
          <p className="text-[9px] uppercase tracking-wide font-semibold" style={{ color: data.variance_value_30d < 0 ? '#991B1B' : '#065F46' }}>Variance (30d)</p>
          <p className="text-lg font-bold" style={{ color: data.variance_value_30d < 0 ? '#DC2626' : '#2D8B5E' }}>{data.variance_value_30d >= 0 ? '+' : ''}{fC(data.variance_value_30d)}</p>
        </div>
        <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-[9px] uppercase tracking-wide" style={{ color: '#9CA3AF' }}>Recent Counts</p>
          <p className="text-lg font-bold" style={{ color: '#1B2A4A' }}>{data.recent_counts.length}</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Bar SKU Inventory</p>
        <div className="flex items-center gap-1.5 flex-wrap">
          {canEdit && <button onClick={() => setCountMode(!countMode)} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded text-white" style={{ backgroundColor: countMode ? '#DC2626' : '#D97706' }} data-testid="bar-count-toggle">{countMode ? 'Cancel Count' : 'Start Count'}</button>}
          {canEdit && countMode && <button onClick={submitCount} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded text-white" style={{ backgroundColor: '#2D8B5E' }} data-testid="bar-count-submit">Submit Count</button>}
          {canEdit && !countMode && <button onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="bar-add-btn"><Plus size={11} /> Add SKU</button>}
          <button onClick={downloadTemplate} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="bar-template-btn"><Download size={11} /> Template</button>
          <button onClick={exportCSV} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="bar-export-btn"><Download size={11} /> Export</button>
          {canEdit && <>
            <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="bar-import-btn"><Upload size={11} /> Import</button>
            <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={importCSV} />
          </>}
        </div>
      </div>

      {/* Add/Edit form */}
      {showForm && (
        <div className="bg-white rounded-xl border p-4 space-y-2" style={{ borderColor: '#2A7B88', borderWidth: 2 }} data-testid="bar-form">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <label className="text-xs" style={{ color: '#6B7280' }}>Item*
              <input value={form.item_name} onChange={e => setForm(f => ({ ...f, item_name: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Category
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }}>
                {(data.categories || []).map(c => <option key={c}>{c}</option>)}
              </select>
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>SKU code
              <input value={form.sku_code} onChange={e => setForm(f => ({ ...f, sku_code: e.target.value }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Bottle ml
              <input type="number" value={form.bottle_size_ml} onChange={e => setForm(f => ({ ...f, bottle_size_ml: parseFloat(e.target.value) || 0 }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Opening stock
              <input type="number" value={form.opening_stock} onChange={e => setForm(f => ({ ...f, opening_stock: parseFloat(e.target.value) || 0 }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Physical count
              <input type="number" value={form.physical_count} onChange={e => setForm(f => ({ ...f, physical_count: parseFloat(e.target.value) || 0 }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Par
              <input type="number" value={form.par_quantity} onChange={e => setForm(f => ({ ...f, par_quantity: parseFloat(e.target.value) || 0 }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
            <label className="text-xs" style={{ color: '#6B7280' }}>Unit cost ₹
              <input type="number" value={form.unit_cost} onChange={e => setForm(f => ({ ...f, unit_cost: parseFloat(e.target.value) || 0 }))} className="w-full px-2 py-1.5 rounded border mt-0.5" style={{ borderColor: '#E5E1DC' }} />
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-xs px-3 py-1.5 rounded border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
            <button onClick={save} className="text-xs px-3 py-1.5 rounded text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="bar-save-btn">{editing ? 'Update' : 'Save'}</button>
          </div>
        </div>
      )}

      {/* SKU Table */}
      {data.skus.length === 0 ? (
        <p className="py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>No SKUs yet. Add items to start tracking bottle-level variance.</p>
      ) : (
        <div className="bg-white rounded-xl border overflow-x-auto" style={{ borderColor: '#E5E1DC' }}>
          <table className="w-full text-xs">
            <thead className="border-b" style={{ backgroundColor: '#F7F5F2', borderColor: '#E5E1DC' }}>
              <tr>
                <th className="text-left py-2 px-3 font-medium" style={{ color: '#6B7280' }}>Item</th>
                <th className="text-left py-2 px-3 font-medium" style={{ color: '#6B7280' }}>Category</th>
                <th className="text-right py-2 px-3 font-medium" style={{ color: '#6B7280' }}>Par</th>
                <th className="text-right py-2 px-3 font-medium" style={{ color: '#6B7280' }}>On-hand</th>
                {countMode && <>
                  <th className="text-right py-2 px-3 font-medium" style={{ color: '#185FA5' }}>Purch.</th>
                  <th className="text-right py-2 px-3 font-medium" style={{ color: '#185FA5' }}>Sales</th>
                  <th className="text-right py-2 px-3 font-medium" style={{ color: '#185FA5' }}>Physical</th>
                </>}
                <th className="text-right py-2 px-3 font-medium" style={{ color: '#6B7280' }}>Unit cost</th>
                <th className="text-right py-2 px-3 font-medium" style={{ color: '#6B7280' }}>Value</th>
                {!countMode && canEdit && <th></th>}
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
              {data.skus.map(s => {
                const shortPar = (s.par_quantity || 0) > (s.physical_count || 0);
                const ce = countEntries[s.id] || {};
                return (
                  <tr key={s.id} style={{ backgroundColor: shortPar && !countMode ? '#FEF2F215' : 'transparent' }}>
                    <td className="py-2 px-3 font-medium" style={{ color: '#1B2A4A' }}>{s.item_name} <span className="text-[10px]" style={{ color: '#9CA3AF' }}>{s.bottle_size_ml}ml</span></td>
                    <td className="py-2 px-3" style={{ color: '#6B7280' }}>{s.category}</td>
                    <td className="py-2 px-3 text-right" style={{ color: '#6B7280' }}>{s.par_quantity}</td>
                    <td className="py-2 px-3 text-right font-medium" style={{ color: shortPar ? '#DC2626' : '#1B2A4A' }}>{s.physical_count}</td>
                    {countMode && <>
                      <td className="py-2 px-2"><input type="number" placeholder="0" className="w-16 text-xs px-1 py-0.5 rounded border text-right" style={{ borderColor: '#E5E1DC' }} value={ce.purchases_since || ''} onChange={e => setCountEntries(prev => ({ ...prev, [s.id]: { ...prev[s.id], purchases_since: e.target.value } }))} /></td>
                      <td className="py-2 px-2"><input type="number" placeholder="0" className="w-16 text-xs px-1 py-0.5 rounded border text-right" style={{ borderColor: '#E5E1DC' }} value={ce.sales_since || ''} onChange={e => setCountEntries(prev => ({ ...prev, [s.id]: { ...prev[s.id], sales_since: e.target.value } }))} /></td>
                      <td className="py-2 px-2"><input type="number" placeholder="count" className="w-16 text-xs px-1 py-0.5 rounded border text-right font-semibold" style={{ borderColor: '#185FA5' }} value={ce.physical_count || ''} onChange={e => setCountEntries(prev => ({ ...prev, [s.id]: { ...prev[s.id], physical_count: e.target.value } }))} data-testid={`bar-count-${s.id}`} /></td>
                    </>}
                    <td className="py-2 px-3 text-right" style={{ color: '#6B7280' }}>{fC(s.unit_cost)}</td>
                    <td className="py-2 px-3 text-right" style={{ color: '#1B2A4A' }}>{fC((s.physical_count || 0) * (s.unit_cost || 0))}</td>
                    {!countMode && canEdit && (
                      <td className="py-2 px-2">
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => startEdit(s)} className="text-[10px] px-1.5 py-0.5 rounded hover:bg-[#F7F5F2]" style={{ color: '#2A7B88' }}>Edit</button>
                          <button onClick={() => del(s.id)} className="text-[10px] px-1.5 py-0.5 rounded hover:bg-red-50" style={{ color: '#D4726A' }}>Del</button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent counts history */}
      {data.recent_counts.length > 0 && (
        <div className="bg-white rounded-xl border p-3" style={{ borderColor: '#E5E1DC' }} data-testid="bar-variance-history">
          <p className="text-xs font-semibold mb-2" style={{ color: '#1B2A4A' }}>Recent Variance Counts (last 30 days)</p>
          <div className="divide-y" style={{ borderColor: '#F3F4F6' }}>
            {data.recent_counts.map(c => (
              <div key={c.id} className="py-2 text-xs">
                <div className="flex items-center justify-between">
                  <span style={{ color: '#1B2A4A' }}>{c.count_date} · {c.counted_by} · {c.total_items} items</span>
                  <span className="font-semibold" style={{ color: c.total_variance_value < 0 ? '#DC2626' : '#2D8B5E' }}>{c.total_variance_value >= 0 ? '+' : ''}{fC(c.total_variance_value)}</span>
                </div>
                {c.items && c.items.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {c.items.filter(i => Math.abs(i.variance_qty) > 0).slice(0, 6).map((i, idx) => (
                      <span key={idx} className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: i.variance_qty < 0 ? '#FEF2F2' : '#ECFDF5', color: i.variance_qty < 0 ? '#991B1B' : '#065F46' }}>{i.item_name}: {i.variance_qty >= 0 ? '+' : ''}{i.variance_qty}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

